import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import dagre from "@dagrejs/dagre";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEventHandler,
  type MutableRefObject
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Background,
  ConnectionMode,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeMouseHandler,
  type Node,
  type NodeMouseHandler,
  type NodeProps,
  type NodeTypes,
  type OnEdgesChange,
  type OnNodesChange,
  type ReactFlowInstance
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { SemanticImportWizard } from "../components/SemanticImportWizard.js";
import { SemanticNoteNode, noteIdOfNode, noteNodeId, type SemanticNoteNodeType } from "../components/SemanticNoteNode.js";
import {
  SemanticObjectNode,
  type SemanticObjectNodeField,
  type SemanticObjectNodeType,
  type NodeHandleSpec
} from "../components/SemanticObjectNode.js";
import type { SemanticSpecImportShape } from "../lib/semanticExcelImport.js";
import { isDanglingFieldRef, pruneRestrictionsToFields, removeFieldFromSpec } from "../lib/semanticMappingIntegrity.js";
import { SEMANTIC_SECURITY_CLASSES, SEMANTIC_SECURITY_CLASS_LABEL, legacyAccessValue } from "../lib/semanticModelVocab.js";
import {
  areaDeletionWarning,
  assignedLine,
  fieldRestriction,
  FLOOR_SET_IN_MASTER,
  floorAccessLabel,
  NOTE_MAX_CHARS,
  noteCountdown,
  setAreaRestriction,
  type AdminSubjectArea,
  type AssignedPerson,
  type FieldRestriction
} from "../lib/subjectAreaAccess.js";
import { IERelationshipEdge, type IERelationshipEdgeType } from "../components/IERelationshipEdge.js";
import { InspectorFlyout } from "../components/inspector/InspectorFlyout.js";
import {
  buildTemplateRows,
  copyIntoModelName,
  excludeConfirmCopy,
  shadowIndicatorFor,
  shouldShowTemplateGroup,
  templateRowActions,
  visibleTemplateRows,
  type TemplateRow,
} from "../lib/semanticTemplateLayer.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

const sqlEditorExtensions = [sql({ dialect: PostgreSQL })];

const SPEC_AUTOSAVE_DEBOUNCE_MS = 450;

/** Aligned with hub `validateSemanticModel` FIELD_DATA_TYPES. */
const SEMANTIC_FIELD_DATA_TYPES = [
  "varchar",
  "int",
  "decimal",
  "boolean",
  "date",
  "datetime",
  "text",
  "blob"
] as const;
const SEMANTIC_FIELD_DATA_TYPE_SET = new Set<string>(SEMANTIC_FIELD_DATA_TYPES);

/** Aligned with hub EXPOSURE_MODES. */
const SEMANTIC_EXPOSURE_MODES = ["full", "masked", "aggregate_only", "restricted"] as const;

// ADR-0154 §6.3 C5 — one console home, parity-gated. "Aligned with hub" was a
// comment; this is the mechanism.
import { SEMANTIC_DISPLAY_MODES } from "../lib/semanticModelVocab.js";
const SEMANTIC_DISPLAY_MODE_SET = new Set<string>(SEMANTIC_DISPLAY_MODES);

type SemanticDisplayMode = (typeof SEMANTIC_DISPLAY_MODES)[number];

const SEMANTIC_DISPLAY_LABELS: Record<SemanticDisplayMode, string> = {
  name: "Name",
  keys: "Keys",
  columns: "Columns",
  profile: "Profile"
};

/** Map JDBC-ish / engine column type strings to semantic model field types. */
function mapInferColumnTypeToSemantic(raw: string): string {
  const t = raw.trim().toLowerCase();
  if (!t) return "varchar";
  if (SEMANTIC_FIELD_DATA_TYPE_SET.has(t)) return t;
  if (/\b(bool|boolean)\b/u.test(t)) return "boolean";
  if (/\b(int|integer|smallint|bigint|serial)\b/u.test(t)) return "int";
  if (/\bnumber\b/u.test(t)) return "decimal";
  if (/\b(decimal|numeric|money|real|double|float)\b/u.test(t)) return "decimal";
  if (/\b(date|time)\b/u.test(t) && !/\b(timestamp|datetime)\b/u.test(t)) return /\btime\b/u.test(t) && !/\bdate\b/u.test(t) ? "datetime" : "date";
  if (/\b(timestamp|datetime)\b/u.test(t)) return "datetime";
  if (/\b(text|clob|longvarchar|json)\b/u.test(t)) return "text";
  if (/\b(blob|binary|bytea|varbinary)\b/u.test(t)) return "blob";
  if (/\b(char|varchar|string|text|uuid)\b/u.test(t)) return "varchar";
  return "varchar";
}

function mergeInferredFields(
  existing: SemanticField[],
  columns: Array<{ name: string; type: string }>
): SemanticField[] {
  const byName = new Map(existing.map((f) => [f.name.toLowerCase(), f] as const));
  return columns.map((col) => {
    const prev = byName.get(col.name.toLowerCase());
    const dt = mapInferColumnTypeToSemantic(col.type);
    if (prev) {
      return { ...prev, name: col.name, data_type: dt };
    }
    return {
      id: crypto.randomUUID(),
      name: col.name,
      data_type: dt,
      primary_key: false,
      security_profile: { security_class: "allow", exposure_mode: "full" }
    };
  });
}

const SEMANTIC_NODE_LAYOUT_WIDTH = 280;

function normalizeDisplayMode(raw: string | undefined): SemanticDisplayMode {
  if (raw && SEMANTIC_DISPLAY_MODE_SET.has(raw)) return raw as SemanticDisplayMode;
  return "columns";
}

function estimateSemanticNodeHeight(displayMode: SemanticDisplayMode, obj: SemanticObject | undefined): number {
  const TITLE = 42;
  const DIV = 2;
  const ROW = 22;
  const PAD = 12;
  const SEP = 6;

  if (!obj || displayMode === "name") return TITLE + PAD;

  const fields = obj.fields ?? [];
  const pks = fields.filter((f) => f.primary_key);
  const nonPk = fields.filter((f) => !f.primary_key);

  if (displayMode === "keys") {
    return TITLE + DIV + pks.length * ROW + PAD;
  }

  const maxNonPk = 10;
  const shownNonPk = Math.min(nonPk.length, maxNonPk);
  const overflow = nonPk.length > maxNonPk ? ROW : 0;
  let h = TITLE + DIV;
  h += pks.length * ROW;
  if (pks.length > 0 && nonPk.length > 0) h += SEP;
  h += shownNonPk * ROW + overflow + PAD;
  return h;
}

type SemanticNodeWall = "top" | "right" | "bottom" | "left";

const SEMANTIC_NODE_WALLS: SemanticNodeWall[] = ["top", "right", "bottom", "left"];

function semanticWallPoint(
  x: number,
  y: number,
  w: number,
  h: number,
  wall: SemanticNodeWall
): { x: number; y: number } {
  const cx = x + w / 2;
  const cy = y + h / 2;
  switch (wall) {
    case "top":
      return { x: cx, y };
    case "right":
      return { x: x + w, y: cy };
    case "bottom":
      return { x: cx, y: y + h };
    case "left":
      return { x, y: cy };
  }
}

/** Pick the walls (not handle IDs) that minimize connector distance between two nodes. */
function pickSemanticRelationshipWalls(
  sourcePos: { x: number; y: number },
  targetPos: { x: number; y: number },
  sourceW: number,
  sourceH: number,
  targetW: number,
  targetH: number,
  isSelfLoop: boolean
): { sw: SemanticNodeWall; tw: SemanticNodeWall } {
  if (isSelfLoop) {
    return { sw: "right", tw: "top" };
  }

  let best: { dist: number; sw: SemanticNodeWall; tw: SemanticNodeWall } | null = null;
  for (const sw of SEMANTIC_NODE_WALLS) {
    const ps = semanticWallPoint(sourcePos.x, sourcePos.y, sourceW, sourceH, sw);
    for (const tw of SEMANTIC_NODE_WALLS) {
      const pt = semanticWallPoint(targetPos.x, targetPos.y, targetW, targetH, tw);
      const dist = (ps.x - pt.x) ** 2 + (ps.y - pt.y) ** 2;
      if (!best || dist < best.dist) {
        best = { dist, sw, tw };
      }
    }
  }
  return { sw: best!.sw, tw: best!.tw };
}

/** Drag payload from object explorer → canvas (add placement or move). */
const SEMANTIC_OBJECT_DRAG_MIME = "application/x-atlas-semantic-object";

type SemanticField = {
  id: string;
  name: string;
  data_type: string;
  primary_key?: boolean;
  description?: string | null;
  output_format?: string | null;
  notes?: string | null;
  security_profile: { security_class: string; exposure_mode: string };
};

type SemanticObject = {
  id: string;
  name: string;
  description?: string | null;
  source: Record<string, unknown>;
  fields: SemanticField[];
};

/**
 * ADR-0161 §7.2 — a served template object, as the hub hands it over.
 * `object` is the RAW semantic.v1 object, so the read-only inspector renders
 * the real thing and "Copy into model" clones the real thing.
 */
type TemplateObjectRow = {
  name: string;
  template_kind: string;
  excluded: boolean;
  object: SemanticObject;
};

/**
 * ADR-0212 §3.3 — `restricted_field_ids`: the columns this (non-Master) area restricts on this object,
 * by field id. Absent means none; never present on a Master placement.
 */
type ObjectPlacement = { object_id: string; ui: { x: number; y: number }; restricted_field_ids?: string[] };

/** ADR-0212 §3.7 — a canvas note: plain text in one subject area, at most 2,000 characters. */
type SemanticNote = { id: string; text: string; ui: { x: number; y: number } };

type SubjectArea = {
  id: string;
  name: string;
  description?: string | null;
  is_master?: boolean;
  display_mode?: string;
  object_placements: ObjectPlacement[];
  notes?: SemanticNote[];
  ui?: { viewport?: { x: number; y: number; zoom: number } } | null;
};

type JoinCondition =
  | { type: "field_mapping"; mappings: Array<{ source_field: string; target_field: string }> }
  | { type: "expression"; expression: string };

type IECardinality = "1:1" | "0:1" | "1:M" | "0:1:M";

type SemanticRelationship = {
  id: string;
  name: string;
  name_auto?: boolean;
  source_object_id: string;
  source_alias: string;
  target_object_id: string;
  target_alias: string;
  join_condition: JoinCondition;
  source_cardinality: IECardinality;
  target_cardinality: IECardinality;
};

type SemanticSpecV1 = {
  version: "semantic.v1";
  objects: SemanticObject[];
  relationships: SemanticRelationship[];
  subject_areas: SubjectArea[];
};

type SemanticModelStatus = "active" | "draft" | "inactive";

type InspectorTopTab = "catalog" | "config";

type ModelConfigSubTab = "model" | "subjectArea";

type ObjectConfigSubTab = "metadata" | "fields" | "source";

type SemanticModelRow = {
  id: string;
  name: string;
  description: string | null;
  status: SemanticModelStatus;
  tags: string[];
  spec: SemanticSpecV1;
  updated_at: string;
  owner_email: string | null;
};

type LastSavedSnapshot = {
  name: string;
  description: string;
  status: string;
  tags: string[];
  specJson: string;
  updatedAt: string;
};

function parseSemanticStatus(raw: string): SemanticModelStatus {
  if (raw === "active" || raw === "draft" || raw === "inactive") return raw;
  return "draft";
}

function canvasSignature(spec: SemanticSpecV1, activeSaId: string): string {
  const sa = spec.subject_areas.find((s) => s.id === activeSaId);
  return JSON.stringify({
    saId: activeSaId,
    display_mode: normalizeDisplayMode(sa?.display_mode),
    placements: sa?.object_placements ?? [],
    notes: sa?.notes ?? [],
    objects: spec.objects.map((o) => ({
      id: o.id,
      name: o.name,
      fields: o.fields.map((f) => ({
        id: f.id,
        name: f.name,
        data_type: f.data_type,
        primary_key: f.primary_key,
        restricted: fieldRestriction(sa, o.id, f),
        exposure_mode: f.security_profile.exposure_mode
      }))
    })),
    relationships: spec.relationships.map((r) => ({
      id: r.id,
      source: r.source_object_id,
      target: r.target_object_id,
      name: r.name
    }))
  });
}

function buildFlowFromSpec(spec: SemanticSpecV1, activeSaId: string): { nodes: Node[]; edges: Edge[] } {
  const sa = spec.subject_areas.find((s) => s.id === activeSaId);
  const displayMode = normalizeDisplayMode(sa?.display_mode);
  const placed = new Set((sa?.object_placements ?? []).map((p) => p.object_id));
  const byId = new Map(spec.objects.map((o) => [o.id, o] as const));
  const nodes: Node[] = (sa?.object_placements ?? []).map((p) => {
    const obj = byId.get(p.object_id);
    const label = obj?.name ?? p.object_id;
    return {
      id: p.object_id,
      type: "semanticObject",
      position: { x: p.ui?.x ?? 0, y: p.ui?.y ?? 0 },
      data: {
        label,
        objectId: p.object_id,
        displayMode,
        fields: (obj?.fields ?? []).map(
          (f): SemanticObjectNodeField => ({
            id: f.id,
            name: f.name,
            data_type: f.data_type,
            primary_key: f.primary_key,
            // ADR-0212 §4.4 — the marker: restricted by the column's own setting, or by this area.
            restricted: fieldRestriction(sa, p.object_id, f),
            exposure_mode: f.security_profile.exposure_mode
          })
        )
      }
    };
  });
  const placementByObject = new Map((sa?.object_placements ?? []).map((p) => [p.object_id, p] as const));

  // Pass 1 — determine which walls each relationship uses, and collect handles per node+wall.
  type WallEntry = { relId: string; side: "source" | "target" };
  const nodeWallEntries = new Map<string, Map<SemanticNodeWall, WallEntry[]>>();
  const relWalls = new Map<string, { sw: SemanticNodeWall; tw: SemanticNodeWall }>();

  const getWallList = (nodeId: string, wall: SemanticNodeWall): WallEntry[] => {
    if (!nodeWallEntries.has(nodeId)) {
      nodeWallEntries.set(nodeId, new Map(SEMANTIC_NODE_WALLS.map((w) => [w, []])));
    }
    return nodeWallEntries.get(nodeId)!.get(wall)!;
  };

  for (const r of spec.relationships) {
    if (!placed.has(r.source_object_id) || !placed.has(r.target_object_id)) continue;
    const sp = placementByObject.get(r.source_object_id);
    const tp = placementByObject.get(r.target_object_id);
    const srcObj = byId.get(r.source_object_id);
    const tgtObj = byId.get(r.target_object_id);
    const sh = estimateSemanticNodeHeight(displayMode, srcObj);
    const th = estimateSemanticNodeHeight(displayMode, tgtObj);
    const walls = pickSemanticRelationshipWalls(
      { x: sp?.ui?.x ?? 0, y: sp?.ui?.y ?? 0 },
      { x: tp?.ui?.x ?? 0, y: tp?.ui?.y ?? 0 },
      SEMANTIC_NODE_LAYOUT_WIDTH, sh,
      SEMANTIC_NODE_LAYOUT_WIDTH, th,
      r.source_object_id === r.target_object_id
    );
    relWalls.set(r.id, walls);
    getWallList(r.source_object_id, walls.sw).push({ relId: r.id, side: "source" });
    getWallList(r.target_object_id, walls.tw).push({ relId: r.id, side: "target" });
  }

  // Pass 2 — assign evenly-distributed offsets along each wall and build handle specs.
  const nodeHandleMap = new Map<string, NodeHandleSpec[]>();
  for (const [nodeId, wallMap] of nodeWallEntries) {
    const specs: NodeHandleSpec[] = [];
    for (const [wall, entries] of wallMap) {
      const n = entries.length;
      if (n === 0) continue;
      entries.forEach(({ relId, side }, i) => {
        specs.push({
          id: side === "source" ? `${wall}-s-${relId}` : `${wall}-t-${relId}`,
          type: side === "source" ? "source" : "target",
          wall,
          offset: ((i + 1) / (n + 1)) * 100
        });
      });
    }
    nodeHandleMap.set(nodeId, specs);
  }

  // Pass 3 — build edges using the per-relationship handle IDs.
  const edges: IERelationshipEdgeType[] = [];
  for (const r of spec.relationships) {
    if (!placed.has(r.source_object_id) || !placed.has(r.target_object_id)) continue;
    const { sw, tw } = relWalls.get(r.id)!;
    edges.push({
      id: r.id,
      source: r.source_object_id,
      target: r.target_object_id,
      sourceHandle: `${sw}-s-${r.id}`,
      targetHandle: `${tw}-t-${r.id}`,
      type: "ieRelationship",
      data: {
        source_cardinality: r.source_cardinality,
        target_cardinality: r.target_cardinality
      }
    });
  }

  // Rebuild nodes with handle specs so SemanticObjectNode renders them at offset positions.
  const nodesWithHandles = nodes.map((n) => ({
    ...n,
    data: { ...n.data, handles: nodeHandleMap.get(n.id) ?? [] }
  }));

  // ADR-0212 §4.6 — the active area's notes: text on the canvas, joined to nothing.
  const noteNodes: SemanticNoteNodeType[] = (sa?.notes ?? []).map((note) => ({
    id: noteNodeId(note.id),
    type: "semanticNote",
    position: { x: note.ui?.x ?? 0, y: note.ui?.y ?? 0 },
    data: { text: note.text ?? "" },
    connectable: false
  }));

  return { nodes: [...nodesWithHandles, ...noteNodes], edges };
}

function applyAutoLayout(spec: SemanticSpecV1, activeSaId: string, allNodes: Node[], edges: Edge[]): SemanticSpecV1 {
  const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
  const sa = next.subject_areas.find((s) => s.id === activeSaId);
  // ADR-0212 §4.6 — auto layout arranges objects; notes stay where they were put.
  const nodes = allNodes.filter((n) => n.type !== "semanticNote");
  if (!sa || nodes.length === 0) return next;

  const dm = normalizeDisplayMode(sa.display_mode);
  const byId = new Map(next.objects.map((o) => [o.id, o] as const));

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 48 });
  for (const n of nodes) {
    const obj = byId.get(n.id);
    const h = estimateSemanticNodeHeight(dm, obj);
    g.setNode(n.id, { width: SEMANTIC_NODE_LAYOUT_WIDTH, height: h });
  }
  for (const e of edges) {
    if (!g.hasNode(e.source) || !g.hasNode(e.target)) continue;
    g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  for (const pl of sa.object_placements) {
    const node = g.node(pl.object_id);
    if (!node) continue;
    const obj = byId.get(pl.object_id);
    const h = estimateSemanticNodeHeight(dm, obj);
    const x = (node.x ?? 0) - SEMANTIC_NODE_LAYOUT_WIDTH / 2;
    const y = (node.y ?? 0) - h / 2;
    pl.ui = { x, y };
  }
  return next;
}

function deleteObjectFromSpec(spec: SemanticSpecV1, objectId: string): SemanticSpecV1 {
  const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
  next.objects = next.objects.filter((o) => o.id !== objectId);
  next.relationships = next.relationships.filter(
    (r) => r.source_object_id !== objectId && r.target_object_id !== objectId
  );
  for (const sa of next.subject_areas) {
    sa.object_placements = sa.object_placements.filter((p) => p.object_id !== objectId);
  }
  return next;
}

function removePlacementFromSpec(spec: SemanticSpecV1, saId: string, objectId: string): SemanticSpecV1 {
  const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
  const sa = next.subject_areas.find((s) => s.id === saId);
  if (sa) {
    sa.object_placements = sa.object_placements.filter((p) => p.object_id !== objectId);
  }
  return next;
}

/** Add placement only if missing (explorer drop); moving uses canvas drag + onNodeDragStop. */
function addPlacementOnlyAt(
  spec: SemanticSpecV1,
  saId: string,
  objectId: string,
  position: { x: number; y: number }
): SemanticSpecV1 {
  if (!spec.objects.some((o) => o.id === objectId)) return spec;
  const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
  const sa = next.subject_areas.find((s) => s.id === saId);
  if (!sa) return next;
  if (sa.object_placements.some((p) => p.object_id === objectId)) return next;
  sa.object_placements.push({ object_id: objectId, ui: { x: position.x, y: position.y } });
  return next;
}

type ColumnFieldPatch = {
  name?: string;
  data_type?: string;
  primary_key?: boolean;
  security_class?: string;
  exposure_mode?: string;
  description?: string | null;
  output_format?: string | null;
  notes?: string | null;
};

function SemanticColumnRow({
  objectId,
  field,
  onApplyPatch,
  onRemoveField,
  onMoveField,
  moveUpDisabled,
  moveDownDisabled,
  removeDisabled,
  onSelectField,
  rowSelected,
  areaRestriction
}: {
  objectId: string;
  field: SemanticField;
  /** ADR-0212 §4.3 — present while a non-Master area that places this object is active: its "In ‹area›" cell. */
  areaRestriction?: { restriction: FieldRestriction; onChange: (restricted: boolean) => void };
  onApplyPatch: (oid: string, fieldId: string, patch: ColumnFieldPatch) => void;
  onRemoveField: (oid: string, fieldId: string) => void;
  onMoveField: (oid: string, fieldId: string, dir: "up" | "down") => void;
  moveUpDisabled?: boolean;
  moveDownDisabled?: boolean;
  removeDisabled?: boolean;
  onSelectField?: () => void;
  rowSelected?: boolean;
}) {
  const [name, setName] = useState(field.name);

  const dataTypeOptions = useMemo(() => {
    const cur = field.data_type;
    const list = [...SEMANTIC_FIELD_DATA_TYPES];
    if (cur && !SEMANTIC_FIELD_DATA_TYPE_SET.has(cur)) {
      return [cur, ...list];
    }
    return list;
  }, [field.data_type]);

  // ADR-0212: allow or restrict, nothing else — a value the hub would refuse is never offered; a missing
  // or old value shows as what it reads as (§6.1).
  const securityClassValue = legacyAccessValue(field.security_profile?.security_class);

  useEffect(() => {
    setName(field.name);
  }, [field.id, field.name, field.data_type, field.security_profile.exposure_mode, field.primary_key]);

  return (
    <tr
      className={`semanticColumnTableRow${rowSelected ? " semanticColumnTableRowSelected" : ""}`}
      onClick={
        onSelectField
          ? (e) => {
              if ((e.target as HTMLElement).closest("input,select,button")) return;
              onSelectField();
            }
          : undefined
      }
      style={onSelectField ? { cursor: "pointer" } : undefined}
    >
      <td>
        <input
          className="input semanticColumnTableInput"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={() => {
            const t = name.trim();
            if (!t) {
              setName(field.name);
              return;
            }
            if (t !== field.name) onApplyPatch(objectId, field.id, { name: t });
          }}
          aria-label="Column name"
        />
      </td>
      <td>
        <select
          className="input semanticColumnTableSelect mono"
          value={
            dataTypeOptions.includes(field.data_type) ? field.data_type : SEMANTIC_FIELD_DATA_TYPES[0]
          }
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onApplyPatch(objectId, field.id, { data_type: e.target.value })}
          aria-label="Data type"
        >
          {dataTypeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </td>
      <td className="semanticColumnTableCellPk">
        <input
          type="checkbox"
          checked={!!field.primary_key}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onApplyPatch(objectId, field.id, { primary_key: e.target.checked })}
          aria-label="Primary key"
        />
      </td>
      <td>
        {areaRestriction ? (
          // ADR-0212 R12 — in an area view the floor is read-only: it is restricted everywhere, and an
          // area view edits only the area's own restriction (the "In ‹area›" cell). The floor is set in Master.
          <span className="muted" title={FLOOR_SET_IN_MASTER} aria-label="Access">
            {floorAccessLabel(field)}
          </span>
        ) : (
          <select
            className="input semanticColumnTableSelect"
            value={securityClassValue}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onApplyPatch(objectId, field.id, { security_class: e.target.value })}
            aria-label="Access"
          >
            {SEMANTIC_SECURITY_CLASSES.map((c) => (
              <option key={c} value={c}>
                {SEMANTIC_SECURITY_CLASS_LABEL[c]}
              </option>
            ))}
          </select>
        )}
      </td>
      {areaRestriction ? (
        <td>
          {areaRestriction.restriction === "everywhere" ? (
            <span className="muted">Restricted everywhere</span>
          ) : (
            <select
              className="input semanticColumnTableSelect"
              value={areaRestriction.restriction === "here" ? "restricted" : "allowed"}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => areaRestriction.onChange(e.target.value === "restricted")}
              aria-label="Access in this subject area"
            >
              <option value="allowed">Allowed</option>
              <option value="restricted">Restricted</option>
            </select>
          )}
        </td>
      ) : null}
      <td>
        <select
          className="input semanticColumnTableSelect"
          value={field.security_profile.exposure_mode}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onApplyPatch(objectId, field.id, { exposure_mode: e.target.value })}
          aria-label="Exposure"
        >
          {SEMANTIC_EXPOSURE_MODES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </td>
      <td className="semanticColumnTableCellReorder">
        <div className="semanticColumnReorderGroup">
          <button
            type="button"
            className="semanticColumnReorderBtn"
            disabled={moveUpDisabled}
            title="Move up"
            aria-label="Move field up"
            onClick={(e) => {
              e.stopPropagation();
              onMoveField(objectId, field.id, "up");
            }}
          >
            ↑
          </button>
          <button
            type="button"
            className="semanticColumnReorderBtn"
            disabled={moveDownDisabled}
            title="Move down"
            aria-label="Move field down"
            onClick={(e) => {
              e.stopPropagation();
              onMoveField(objectId, field.id, "down");
            }}
          >
            ↓
          </button>
        </div>
      </td>
      <td className="semanticColumnTableCellDelete">
        <button
          type="button"
          className="semanticColumnDeleteBtn"
          disabled={removeDisabled}
          title={removeDisabled ? "Cannot remove columns from query-backed objects" : "Remove column"}
          aria-label="Remove column"
          onClick={(e) => {
            e.stopPropagation();
            onRemoveField(objectId, field.id);
          }}
        >
          <svg
            className="semanticColumnDeleteIcon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

function RelationshipInspector({
  relationship,
  spec,
  onApplyPatch,
  onSelectObject
}: {
  relationship: SemanticRelationship;
  spec: SemanticSpecV1;
  onApplyPatch: (relId: string, patch: Partial<SemanticRelationship>) => void;
  onSelectObject: (oid: string) => void;
}) {
  const srcObj = spec.objects.find((o) => o.id === relationship.source_object_id);
  const tgtObj = spec.objects.find((o) => o.id === relationship.target_object_id);

  const [nameDraft, setNameDraft] = useState(relationship.name);
  const [srcAliasDraft, setSrcAliasDraft] = useState(relationship.source_alias);
  const [tgtAliasDraft, setTgtAliasDraft] = useState(relationship.target_alias);
  const [joinMode, setJoinMode] = useState<"field_mapping" | "expression">(relationship.join_condition.type);

  useEffect(() => {
    setNameDraft(relationship.name);
    setSrcAliasDraft(relationship.source_alias);
    setTgtAliasDraft(relationship.target_alias);
    setJoinMode(relationship.join_condition.type);
  }, [relationship.id, relationship.name, relationship.source_alias, relationship.target_alias, relationship.join_condition.type]);

  const srcFields = srcObj?.fields ?? [];
  const tgtFields = tgtObj?.fields ?? [];

  const mappings =
    relationship.join_condition.type === "field_mapping" ? relationship.join_condition.mappings : [];
  const expression =
    relationship.join_condition.type === "expression" ? relationship.join_condition.expression : "";

  const addMapping = () => {
    const next: SemanticRelationship["join_condition"] = {
      type: "field_mapping",
      mappings: [...mappings, { source_field: srcFields[0]?.id ?? "", target_field: tgtFields[0]?.id ?? "" }]
    };
    onApplyPatch(relationship.id, { join_condition: next });
  };

  const moveMapping = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= mappings.length) return;
    const next = [...mappings];
    [next[i], next[j]] = [next[j], next[i]];
    onApplyPatch(relationship.id, { join_condition: { type: "field_mapping", mappings: next } });
  };

  const removeMapping = (i: number) => {
    const next: SemanticRelationship["join_condition"] = {
      type: "field_mapping",
      mappings: mappings.filter((_, idx) => idx !== i)
    };
    onApplyPatch(relationship.id, { join_condition: next });
  };

  const updateMapping = (i: number, field: "source_field" | "target_field", value: string) => {
    const next: SemanticRelationship["join_condition"] = {
      type: "field_mapping",
      mappings: mappings.map((m, idx) => (idx === i ? { ...m, [field]: value } : m))
    };
    onApplyPatch(relationship.id, { join_condition: next });
  };

  const switchJoinMode = (mode: "field_mapping" | "expression") => {
    if (mode === joinMode) return;
    setJoinMode(mode);
    if (mode === "field_mapping") {
      onApplyPatch(relationship.id, { join_condition: { type: "field_mapping", mappings } });
    } else {
      onApplyPatch(relationship.id, { join_condition: { type: "expression", expression } });
    }
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="field">
        <label className="label" htmlFor="sem-rel-name">
          Name
          {relationship.name_auto && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                background: "var(--bg-subtle, #f1f5f9)",
                padding: "1px 5px",
                borderRadius: 4,
                color: "var(--muted)"
              }}
            >
              auto
            </span>
          )}
        </label>
        <input
          id="sem-rel-name"
          className="input"
          value={nameDraft}
          onChange={(e) => {
            setNameDraft(e.target.value);
          }}
          onBlur={() => {
            const trimmed = nameDraft.trim();
            if (!trimmed || trimmed === relationship.name) return;
            onApplyPatch(relationship.id, { name: trimmed, name_auto: false });
          }}
        />
        {relationship.name_auto ? null : (
          <button
            className="btn btnSm"
            style={{ marginTop: 4 }}
            type="button"
            onClick={() => {
              const derived = `${relationship.source_alias} → ${relationship.target_alias}`;
              onApplyPatch(relationship.id, { name: derived, name_auto: true });
              setNameDraft(derived);
            }}
          >
            Reset to auto
          </button>
        )}
      </div>

      <div className="reportSectionTitle">Source and target</div>
      <div className="tableWrap semanticRelEndpointsTableWrap">
        <table className="table tableCompact semanticRelEndpointsTable">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">Target</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <button
                  type="button"
                  className="semanticRelEndpointObjectLink"
                  onClick={() => onSelectObject(relationship.source_object_id)}
                  aria-label={`Open object ${srcObj?.name ?? relationship.source_object_id} in inspector`}
                >
                  {srcObj?.name ?? relationship.source_object_id}
                </button>
              </td>
              <td>
                <button
                  type="button"
                  className="semanticRelEndpointObjectLink"
                  onClick={() => onSelectObject(relationship.target_object_id)}
                  aria-label={`Open object ${tgtObj?.name ?? relationship.target_object_id} in inspector`}
                >
                  {tgtObj?.name ?? relationship.target_object_id}
                </button>
              </td>
            </tr>
            <tr>
              <td>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="label" htmlFor="sem-rel-src-alias">
                    Alias
                  </label>
                  <input
                    id="sem-rel-src-alias"
                    className="input"
                    value={srcAliasDraft}
                    onChange={(e) => setSrcAliasDraft(e.target.value)}
                    onBlur={() => {
                      const t = srcAliasDraft.trim();
                      if (!t || t === relationship.source_alias) return;
                      onApplyPatch(relationship.id, { source_alias: t });
                    }}
                  />
                </div>
              </td>
              <td>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="label" htmlFor="sem-rel-tgt-alias">
                    Alias
                  </label>
                  <input
                    id="sem-rel-tgt-alias"
                    className="input"
                    value={tgtAliasDraft}
                    onChange={(e) => setTgtAliasDraft(e.target.value)}
                    onBlur={() => {
                      const t = tgtAliasDraft.trim();
                      if (!t || t === relationship.target_alias) return;
                      onApplyPatch(relationship.id, { target_alias: t });
                    }}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="label" htmlFor="sem-rel-src-card">
                    Cardinality
                  </label>
                  <select
                    id="sem-rel-src-card"
                    className="input"
                    value={relationship.source_cardinality}
                    onChange={(e) =>
                      onApplyPatch(relationship.id, { source_cardinality: e.target.value as IECardinality })
                    }
                  >
                    <option value="1:1">1:1 — exactly one</option>
                    <option value="0:1">0:1 — zero or one</option>
                  </select>
                </div>
              </td>
              <td>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="label" htmlFor="sem-rel-tgt-card">
                    Cardinality
                  </label>
                  <select
                    id="sem-rel-tgt-card"
                    className="input"
                    value={relationship.target_cardinality}
                    onChange={(e) =>
                      onApplyPatch(relationship.id, { target_cardinality: e.target.value as IECardinality })
                    }
                  >
                    <option value="1:1">1:1 — exactly one</option>
                    <option value="0:1">0:1 — zero or one</option>
                    <option value="1:M">1:M — one or many</option>
                    <option value="0:1:M">0:1:M — zero or many</option>
                  </select>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Join Condition */}
      <div className="reportSectionTitle">Join condition</div>
      <div className="tabs" style={{ marginBottom: 4 }}>
        <button
          type="button"
          className={`tab${joinMode === "field_mapping" ? " tabActive" : ""}`}
          onClick={() => switchJoinMode("field_mapping")}
        >
          Field mapping
        </button>
        <button
          type="button"
          className={`tab${joinMode === "expression" ? " tabActive" : ""}`}
          onClick={() => switchJoinMode("expression")}
        >
          Expression
        </button>
      </div>

      {joinMode === "field_mapping" ? (
        <div>
          {mappings.length > 0 && (
            <table className="table tableCompact" style={{ marginBottom: 6 }}>
              <thead>
                <tr>
                  <th>Source field</th>
                  <th>Target field</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m, i) => (
                  <tr key={i}>
                    <td>
                      <select
                        className="input"
                        style={{ padding: "2px 4px", fontSize: 12 }}
                        value={m.source_field}
                        onChange={(e) => updateMapping(i, "source_field", e.target.value)}
                        aria-invalid={isDanglingFieldRef(srcFields, m.source_field) || undefined}
                      >
                        {isDanglingFieldRef(srcFields, m.source_field) && (
                          // A value that matches no option would otherwise DISPLAY the first
                          // field and read as a valid join. Name the state instead.
                          <option value={m.source_field} disabled>
                            Missing column — choose one
                          </option>
                        )}
                        {srcFields.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="input"
                        style={{ padding: "2px 4px", fontSize: 12 }}
                        value={m.target_field}
                        onChange={(e) => updateMapping(i, "target_field", e.target.value)}
                        aria-invalid={isDanglingFieldRef(tgtFields, m.target_field) || undefined}
                      >
                        {isDanglingFieldRef(tgtFields, m.target_field) && (
                          // A value that matches no option would otherwise DISPLAY the first
                          // field and read as a valid join. Name the state instead.
                          <option value={m.target_field} disabled>
                            Missing column — choose one
                          </option>
                        )}
                        {tgtFields.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ width: 72, whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        className="btn btnSm"
                        onClick={() => moveMapping(i, -1)}
                        disabled={i === 0}
                        aria-label="Move mapping up"
                        style={{ padding: "1px 5px" }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btnSm"
                        onClick={() => moveMapping(i, 1)}
                        disabled={i === mappings.length - 1}
                        aria-label="Move mapping down"
                        style={{ padding: "1px 5px", margin: "0 2px" }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btnSm"
                        onClick={() => removeMapping(i)}
                        aria-label="Remove mapping"
                        style={{ padding: "1px 6px" }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button type="button" className="btn btnSm" onClick={addMapping}>
            + Add mapping
          </button>
        </div>
      ) : (
        <div className="field">
          <textarea
            className="input reportTextarea mono"
            rows={4}
            value={expression}
            onChange={(e) => {
              onApplyPatch(relationship.id, { join_condition: { type: "expression", expression: e.target.value } });
            }}
            placeholder="e.g. source.id = target.order_id"
          />
        </div>
      )}
    </div>
  );
}

type FlowCanvasWithDropProps = {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: OnNodesChange<Node>;
  onEdgesChange: OnEdgesChange;
  onNodeClick: NodeMouseHandler;
  onEdgeClick: EdgeMouseHandler;
  onPaneClick: () => void;
  onNodeDragStop: (_evt: unknown, node: Node) => void;
  onNodesDelete: (deleted: Node[]) => void;
  onConnect: (connection: Connection) => void;
  nodeTypes: NodeTypes;
  edgeTypes: { ieRelationship: typeof IERelationshipEdge };
  onCatalogDropOnCanvas: (objectId: string, flowPosition: { x: number; y: number }) => void;
  flowInstanceRef: MutableRefObject<ReactFlowInstance | null>;
  /** The canvas host, so "+ Note" can place a note at the centre of what is visible. */
  hostRef: MutableRefObject<HTMLDivElement | null>;
};

function FlowCanvasWithDrop({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  onNodeDragStop,
  onNodesDelete,
  onConnect,
  nodeTypes,
  edgeTypes,
  onCatalogDropOnCanvas,
  flowInstanceRef,
  hostRef
}: FlowCanvasWithDropProps) {
  const { screenToFlowPosition } = useReactFlow();

  const onDragOver = useCallback<DragEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }, []);

  const onDrop = useCallback<DragEventHandler<HTMLDivElement>>(
    (e) => {
      e.preventDefault();
      const raw = e.dataTransfer?.getData(SEMANTIC_OBJECT_DRAG_MIME) ?? "";
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw) as { objectId?: string };
        if (!parsed.objectId) return;
        const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        onCatalogDropOnCanvas(parsed.objectId, position);
      } catch {
        /* ignore malformed */
      }
    },
    [screenToFlowPosition, onCatalogDropOnCanvas]
  );

  return (
    <div className="semanticDesignerFlowHost" ref={hostRef}>
      <ReactFlow
        onInit={(inst) => {
          flowInstanceRef.current = inst;
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onNodeDragStop={onNodeDragStop}
        onNodesDelete={onNodesDelete}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        onDrop={onDrop}
        onDragOver={onDragOver}
        fitView
        deleteKeyCode={["Backspace", "Delete"]}
        defaultEdgeOptions={{ type: "ieRelationship" }}
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
}

export function SemanticDesigner() {
  const { id: modelId = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modelName, setModelName] = useState("");
  const [modelDescription, setModelDescription] = useState("");
  const [modelTags, setModelTags] = useState<string[]>([]);
  const [rowStatus, setRowStatus] = useState<SemanticModelStatus>("draft");
  const [updatedAt, setUpdatedAt] = useState<string>("");
  const [spec, setSpec] = useState<SemanticSpecV1 | null>(null);
  const [activeSaId, setActiveSaId] = useState<string>("");
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [inspectorTopTab, setInspectorTopTab] = useState<InspectorTopTab>("config");
  const [modelConfigSubTab, setModelConfigSubTab] = useState<ModelConfigSubTab>("model");
  const [objectConfigSubTab, setObjectConfigSubTab] = useState<ObjectConfigSubTab>("metadata");
  const [catalogSearch, setCatalogSearch] = useState("");
  /**
   * ADR-0161 §7.2 — the served template layer. Tenant-scoped, not model-scoped:
   * which standard objects exist depends on the tenant's connector kinds, not on
   * which model happens to be open, so this loads once rather than per model.
   */
  const [templateObjects, setTemplateObjects] = useState<TemplateObjectRow[]>([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState<string | null>(null);
  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  /** Non-master: user chose Delete on canvas; pick SA-only vs full model removal. */
  const [pendingSaObjectDeleteId, setPendingSaObjectDeleteId] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState("");
  const [objectNameDraft, setObjectNameDraft] = useState("");
  const [objectDescDraft, setObjectDescDraft] = useState("");
  const [sourceTypeDraft, setSourceTypeDraft] = useState("view");
  const [sourceNameDraft, setSourceNameDraft] = useState("");
  const [sourceConnectorDraft, setSourceConnectorDraft] = useState("");
  const [connectors, setConnectors] = useState<Array<{ id: string; name: string }>>([]);
  const [inferLoading, setInferLoading] = useState(false);
  const [saNameDraft, setSaNameDraft] = useState("");
  /** ADR-0212 §4.3 — who holds each area of this model (the "Assigned to" line and the deletion warning). */
  const [areaAssignees, setAreaAssignees] = useState<Map<string, AssignedPerson[]> | null>(null);
  const [areaAssigneesError, setAreaAssigneesError] = useState<string | null>(null);
  /** The area whose Name field should take focus once its Subject area tab renders (a new area). */
  const [focusSaNameFor, setFocusSaNameFor] = useState<string | null>(null);
  const saNameInputRef = useRef<HTMLInputElement | null>(null);
  const [saDescDraft, setSaDescDraft] = useState("");
  const [fieldDetailNameDraft, setFieldDetailNameDraft] = useState("");
  const [fieldDetailDescDraft, setFieldDetailDescDraft] = useState("");
  const [fieldDetailOutputDraft, setFieldDetailOutputDraft] = useState("");
  const [fieldDetailNotesDraft, setFieldDetailNotesDraft] = useState("");
  const [fieldDetailSecurityClassDraft, setFieldDetailSecurityClassDraft] = useState("allow");
  const [fieldDetailExposureDraft, setFieldDetailExposureDraft] = useState("full");
  const [fieldDetailTypeDraft, setFieldDetailTypeDraft] = useState("varchar");
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  // ADR-0129 — inspector flyout overlay. Measure the canvas top via a callback
  // ref (survives the loading→loaded remount) + a document observer (catches
  // later layout shifts that move the canvas down). No magic offset.
  const semInspectorAreaElRef = useRef<HTMLElement | null>(null);
  const [semInspectorFlyoutTop, setSemInspectorFlyoutTop] = useState(0);
  const measureSemInspectorTop = useCallback(() => {
    const el = semInspectorAreaElRef.current;
    if (el) setSemInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  const semInspectorAreaRef = useCallback((el: HTMLElement | null) => {
    semInspectorAreaElRef.current = el;
    if (el) setSemInspectorFlyoutTop(el.getBoundingClientRect().top + window.scrollY);
  }, []);
  useEffect(() => {
    measureSemInspectorTop();
    window.addEventListener("resize", measureSemInspectorTop);
    window.addEventListener("scroll", measureSemInspectorTop, true);
    const ro = new ResizeObserver(measureSemInspectorTop);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("resize", measureSemInspectorTop);
      window.removeEventListener("scroll", measureSemInspectorTop, true);
      ro.disconnect();
    };
  }, [measureSemInspectorTop]);
  const [saving, setSaving] = useState(false);

  const modelIdResetRef = useRef<string | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState<Edge>([]);

  const specRef = useRef<SemanticSpecV1 | null>(null);
  const modelNameRef = useRef("");
  const modelDescriptionRef = useRef("");
  const modelTagsRef = useRef<string[]>([]);
  const rowStatusRef = useRef<SemanticModelStatus>("draft");
  const flowInstanceRef = useRef<ReactFlowInstance | null>(null);
  const flowHostRef = useRef<HTMLDivElement | null>(null);
  /** ADR-0212 §4.6 — the selected canvas note (in the active area), its inspector draft, and a pending focus. */
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [noteTextDraft, setNoteTextDraft] = useState("");
  const [focusNoteFor, setFocusNoteFor] = useState<string | null>(null);
  const noteTextRef = useRef<HTMLTextAreaElement | null>(null);
  specRef.current = spec;
  modelNameRef.current = modelName;
  modelDescriptionRef.current = modelDescription;
  modelTagsRef.current = modelTags;
  rowStatusRef.current = rowStatus;

  const lastSavedRef = useRef<LastSavedSnapshot | null>(null);
  const specDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistChainRef = useRef(Promise.resolve());

  const onSemanticFieldRowClick = useCallback((objectId: string, fieldId: string) => {
    setInspectorTopTab("config");
    setSelectedRelationshipId(null);
    setSelectedObjectId(objectId);
    setSelectedFieldId(fieldId);
    setObjectConfigSubTab("fields");
  }, []);

  const nodeTypes = useMemo(
    () => ({
      semanticObject: (p: NodeProps<SemanticObjectNodeType>) => (
        <SemanticObjectNode {...p} onFieldRowClick={(fid) => onSemanticFieldRowClick(p.id, fid)} />
      ),
      semanticNote: SemanticNoteNode
    }),
    [onSemanticFieldRowClick]
  );
  const edgeTypes = useMemo(() => ({ ieRelationship: IERelationshipEdge }), []);

  const canvasSig = useMemo(() => (spec && activeSaId ? canvasSignature(spec, activeSaId) : ""), [spec, activeSaId]);

  useEffect(() => {
    if (!spec || !activeSaId) return;
    const { nodes: n, edges: e } = buildFlowFromSpec(spec, activeSaId);
    setNodes(n);
    setEdges(e);
  }, [canvasSig, spec, activeSaId, setNodes, setEdges]);

  /** Catalog / inspector selection must mirror React Flow `selected` (full node rebuilds clear it otherwise). */
  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        selected:
          (selectedObjectId !== null && n.id === selectedObjectId) ||
          (selectedNoteId !== null && n.id === noteNodeId(selectedNoteId))
      }))
    );
  }, [selectedObjectId, selectedNoteId, canvasSig, setNodes]);

  const clearSpecDebounce = useCallback(() => {
    if (specDebounceRef.current) {
      clearTimeout(specDebounceRef.current);
      specDebounceRef.current = null;
    }
  }, []);

  const applyServerRow = useCallback((m: SemanticModelRow) => {
    const sp = m.spec as SemanticSpecV1;
    const st = parseSemanticStatus(m.status);
    const tg = Array.isArray(m.tags) ? [...m.tags] : [];
    setUpdatedAt(m.updated_at);
    setRowStatus(st);
    rowStatusRef.current = st;
    setSpec(sp);
    setModelName(m.name);
    setModelDescription(m.description ?? "");
    setModelTags(tg);
    modelTagsRef.current = tg;
    lastSavedRef.current = {
      name: m.name,
      description: m.description ?? "",
      status: st,
      tags: tg,
      specJson: JSON.stringify(sp),
      updatedAt: m.updated_at
    };
  }, []);

  const executePatch = useCallback(
    async (overrideSpec?: SemanticSpecV1) => {
      const s = overrideSpec ?? specRef.current;
      if (!s || !lastSavedRef.current) return;
      clearSpecDebounce();
      setSaving(true);
      try {
        const data = await apiFetch<{ semantic_model: SemanticModelRow }>(
          `/api/tenant/semantic-models/${encodeURIComponent(modelId)}`,
          {
            method: "PATCH",
            body: {
              name: modelNameRef.current.trim(),
              description: modelDescriptionRef.current.trim() || null,
              status: rowStatusRef.current,
              tags: modelTagsRef.current,
              spec: s,
              expected_updated_at: lastSavedRef.current.updatedAt
            }
          }
        );
        // Only update conflict-detection state; do NOT call applyServerRow/setSpec here.
        // Overwriting in-memory spec with the server response would discard any changes
        // (e.g. node drags) that occurred while this PATCH was in flight, causing snap-back.
        lastSavedRef.current = {
          ...lastSavedRef.current!,
          specJson: JSON.stringify(s),
          updatedAt: data.semantic_model.updated_at
        };
        setUpdatedAt(data.semantic_model.updated_at);
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 409) {
          toast.push({ kind: "error", message: "This model was changed elsewhere. Reloading." });
          await loadRef.current();
        } else {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      } finally {
        setSaving(false);
      }
    },
    [modelId, clearSpecDebounce, applyServerRow, toast]
  );

  const persistNow = useCallback(
    (overrideSpec?: SemanticSpecV1) => {
      // Capture specRef.current now so the queued PATCH carries the right snapshot
      // even if specRef is overwritten before the chain runs.
      const capturedSpec = overrideSpec ?? specRef.current;
      persistChainRef.current = persistChainRef.current
        .then(async () => {
          clearSpecDebounce();
          await executePatch(capturedSpec ?? undefined);
        })
        .catch(() => {});
    },
    [executePatch, clearSpecDebounce]
  );

  /** Keyboard / UI edge removal must drop the relationship from the spec (edges are derived from spec). */
  const handleEdgesChange = useCallback<OnEdgesChange>(
    (changes) => {
      const removedIds = changes
        .filter((c): c is { type: "remove"; id: string } => c.type === "remove")
        .map((c) => c.id);
      if (removedIds.length > 0 && spec) {
        const idSet = new Set(removedIds);
        if (spec.relationships.some((r) => idSet.has(r.id))) {
          const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
          copy.relationships = copy.relationships.filter((r) => !idSet.has(r.id));
          setSpec(copy);
          if (selectedRelationshipId && idSet.has(selectedRelationshipId)) {
            setSelectedRelationshipId(null);
            setSelectedFieldId(null);
            setInspectorTopTab("config");
          }
          clearSpecDebounce();
          void persistNow(copy);
        }
      }
      onEdgesChangeInternal(changes);
    },
    [spec, selectedRelationshipId, onEdgesChangeInternal, clearSpecDebounce, persistNow]
  );

  const loadRef = useRef<() => Promise<void>>(async () => {});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ semantic_model: SemanticModelRow }>(
        `/api/tenant/semantic-models/${encodeURIComponent(modelId)}`
      );
      const m = data.semantic_model;
      const sp = m.spec as SemanticSpecV1;
      if (sp?.version !== "semantic.v1") {
        setError("Model spec is not semantic.v1");
        setSpec(null);
        lastSavedRef.current = null;
        return;
      }
      applyServerRow(m);
      const master = sp.subject_areas.find((s) => s.is_master);
      setActiveSaId((prev) => {
        if (prev && sp.subject_areas.some((s) => s.id === prev)) return prev;
        return master?.id ?? sp.subject_areas[0]?.id ?? "";
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [modelId, applyServerRow]);

  loadRef.current = load;

  const loadAreaAssignees = useCallback(async (): Promise<Map<string, AssignedPerson[]> | null> => {
    try {
      const data = await apiFetch<{ subject_areas: AdminSubjectArea[] }>("/api/tenant/admin/subject-areas");
      const byArea = new Map(data.subject_areas.filter((a) => a.model_id === modelId).map((a) => [a.id, a.assigned] as const));
      setAreaAssignees(byArea);
      setAreaAssigneesError(null);
      return byArea;
    } catch (e) {
      setAreaAssigneesError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [modelId]);

  useEffect(() => {
    void loadAreaAssignees();
  }, [loadAreaAssignees]);

  useEffect(() => {
    if (modelIdResetRef.current === modelId) return;
    modelIdResetRef.current = modelId;
    setInspectorTopTab("config");
    setSelectedObjectId(null);
    setSelectedRelationshipId(null);
    setSelectedFieldId(null);
    setObjectConfigSubTab("metadata");
    setModelConfigSubTab("model");
  }, [modelId]);

  useEffect(() => {
    setPendingSaObjectDeleteId(null);
  }, [activeSaId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await apiFetch<{ connectors: Array<{ id: string; name: string }> }>("/api/tenant/connectors");
        setConnectors(Array.isArray(data.connectors) ? data.connectors : []);
      } catch {
        setConnectors([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading || !spec || !lastSavedRef.current) return;
    if (JSON.stringify(spec) === lastSavedRef.current.specJson) return;
    if (specDebounceRef.current) clearTimeout(specDebounceRef.current);
    specDebounceRef.current = setTimeout(() => {
      specDebounceRef.current = null;
      void persistNow(undefined);
    }, SPEC_AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (specDebounceRef.current) {
        clearTimeout(specDebounceRef.current);
        specDebounceRef.current = null;
      }
    };
  }, [spec, loading, persistNow]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        void persistNow(undefined);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [persistNow]);

  const activeSa = spec?.subject_areas.find((s) => s.id === activeSaId);
  const isMasterActive = !!activeSa?.is_master;

  const selectedObject = spec?.objects.find((o) => o.id === selectedObjectId);

  // ADR-0161 — the two selections are mutually exclusive: a tenant object and a
  // template object would otherwise render two inspectors into one frame.
  useEffect(() => {
    if (selectedObjectId || selectedRelationshipId) setSelectedTemplateName(null);
  }, [selectedObjectId, selectedRelationshipId]);

  // A note is one selection among the others: selecting anything else, or switching areas, lets it go.
  useEffect(() => {
    if (selectedObjectId || selectedRelationshipId || selectedTemplateName) setSelectedNoteId(null);
  }, [selectedObjectId, selectedRelationshipId, selectedTemplateName]);
  useEffect(() => {
    setSelectedNoteId(null);
  }, [activeSaId]);

  useEffect(() => {
    if (selectedObject) {
      setObjectNameDraft(selectedObject.name);
      setObjectDescDraft(selectedObject.description ?? "");
      const srcType = String(selectedObject.source?.type ?? "view");
      setSourceTypeDraft(srcType);
      const srcRaw = selectedObject.source as Record<string, unknown> | undefined;
      if (srcType === "query") {
        setSourceNameDraft(String(srcRaw?.sql ?? ""));
        setSourceConnectorDraft(String(srcRaw?.connector_id ?? ""));
      } else {
        setSourceNameDraft(String(srcRaw?.name ?? ""));
        setSourceConnectorDraft("");
      }
    } else {
      setObjectNameDraft("");
      setObjectDescDraft("");
      setSourceTypeDraft("view");
      setSourceNameDraft("");
      setSourceConnectorDraft("");
    }
  }, [
    selectedObjectId,
    selectedObject?.name,
    selectedObject?.description,
    String(selectedObject?.source?.type ?? ""),
    String((selectedObject?.source as Record<string, unknown> | undefined)?.name ?? ""),
    String((selectedObject?.source as Record<string, unknown> | undefined)?.sql ?? ""),
    String((selectedObject?.source as Record<string, unknown> | undefined)?.connector_id ?? "")
  ]);

  useEffect(() => {
    setObjectConfigSubTab("metadata");
    setSelectedFieldId(null);
  }, [selectedObjectId]);

  const onNodeClick: NodeMouseHandler = useCallback((_evt, node) => {
    setInspectorTopTab("config");
    const noteId = noteIdOfNode(node.id);
    setSelectedNoteId(noteId);
    setSelectedObjectId(noteId ? null : node.id);
    setSelectedRelationshipId(null);
    setSelectedFieldId(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setInspectorTopTab("config");
    setSelectedObjectId(null);
    setSelectedRelationshipId(null);
    setSelectedFieldId(null);
    setSelectedNoteId(null);
  }, []);

  const onEdgeClick: EdgeMouseHandler = useCallback((_evt, edge) => {
    setInspectorTopTab("config");
    setSelectedRelationshipId(edge.id);
    setSelectedObjectId(null);
    setSelectedFieldId(null);
    setSelectedNoteId(null);
  }, []);

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!spec || !connection.source || !connection.target) return;
      const srcObj = spec.objects.find((o) => o.id === connection.source);
      const tgtObj = spec.objects.find((o) => o.id === connection.target);
      if (!srcObj || !tgtObj) return;

      const toAlias = (name: string) =>
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, "");

      const srcAlias = toAlias(srcObj.name);
      const tgtAlias = toAlias(tgtObj.name);
      const baseName = `${srcAlias} → ${tgtAlias}`;

      const existingNames = new Set(spec.relationships.map((r) => r.name));
      let name = baseName;
      let suffix = 2;
      while (existingNames.has(name)) {
        name = `${baseName}_${suffix++}`;
      }

      const rid = crypto.randomUUID();
      const normName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const tgtByNorm = new Map((tgtObj.fields ?? []).map((f) => [normName(f.name), f.id]));
      const autoMappings = (srcObj.fields ?? [])
        .filter((f) => f.primary_key && tgtByNorm.has(normName(f.name)))
        .map((f) => ({ source_field: f.id, target_field: tgtByNorm.get(normName(f.name))! }));
      const newRel: SemanticRelationship = {
        id: rid,
        name,
        name_auto: true,
        source_object_id: connection.source,
        source_alias: srcAlias,
        target_object_id: connection.target,
        target_alias: tgtAlias,
        join_condition: { type: "field_mapping", mappings: autoMappings },
        source_cardinality: "0:1",
        target_cardinality: "0:1:M"
      };

      const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
      copy.relationships.push(newRel);
      setSpec(copy);
      setSelectedRelationshipId(rid);
      setSelectedObjectId(null);
      setSelectedFieldId(null);
      setInspectorTopTab("config");
      clearSpecDebounce();
      void persistNow(copy);
    },
    [spec, clearSpecDebounce, persistNow]
  );

  const applyRelationshipPatch = useCallback(
    (relId: string, patch: Partial<SemanticRelationship>) => {
      if (!spec) return;
      const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
      const rel = copy.relationships.find((r) => r.id === relId);
      if (!rel) return;
      Object.assign(rel, patch);
      // Keep name in sync if auto-derive is on
      if (rel.name_auto && (patch.source_alias !== undefined || patch.target_alias !== undefined)) {
        rel.name = `${rel.source_alias} → ${rel.target_alias}`;
      }
      setSpec(copy);
      clearSpecDebounce();
      void persistNow(copy);
    },
    [spec, clearSpecDebounce, persistNow]
  );

  const onNodeDragStop = useCallback(
    (_evt: unknown, node: Node) => {
      setSpec((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev)) as SemanticSpecV1;
        const sa = next.subject_areas.find((s) => s.id === activeSaId);
        const noteId = noteIdOfNode(node.id);
        if (noteId) {
          // ADR-0212 §4.6 — a note keeps where it was dropped.
          const note = sa?.notes?.find((n) => n.id === noteId);
          if (note) note.ui = { x: node.position.x, y: node.position.y };
          return next;
        }
        const pl = sa?.object_placements.find((p) => p.object_id === node.id);
        if (pl) {
          pl.ui = { x: node.position.x, y: node.position.y };
        }
        return next;
      });
    },
    [activeSaId]
  );

  const onNodesDelete = useCallback(
    async (deleted: Node[]) => {
      const first = deleted[0];
      if (!first || !spec) return;

      const sync = buildFlowFromSpec(spec, activeSaId);
      setNodes(sync.nodes);
      setEdges(sync.edges);

      const noteId = noteIdOfNode(first.id);
      if (noteId) {
        const ok = await confirm.confirm({ title: "Delete this note?", confirmLabel: "Delete", destructive: true });
        if (!ok) return;
        const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
        const sa = next.subject_areas.find((s) => s.id === activeSaId);
        if (sa?.notes) {
          sa.notes = sa.notes.filter((n) => n.id !== noteId);
          if (sa.notes.length === 0) delete sa.notes;
        }
        setSpec(next);
        setSelectedNoteId(null);
        return;
      }

      if (isMasterActive) {
        const ok = await confirm.confirm({
          title: "Remove object from model?",
          detail: "This removes the object from all subject areas and deletes its relationships.",
          confirmLabel: "Remove"
        });
        if (!ok) return;
        setSpec(deleteObjectFromSpec(spec, first.id));
      } else {
        setPendingSaObjectDeleteId(first.id);
        return;
      }

      setInspectorTopTab("config");
      setSelectedObjectId(null);
      setSelectedRelationshipId(null);
      setSelectedFieldId(null);
    },
    [spec, activeSaId, isMasterActive, confirm, setNodes, setEdges]
  );

  // -------------------------------------------------------------------------
  // ADR-0161 §7.2 — the served template layer
  // -------------------------------------------------------------------------

  const loadTemplateObjects = useCallback(async () => {
    try {
      const res = await apiFetch<{ template_objects: TemplateObjectRow[] }>(
        "/api/tenant/semantic-template-objects"
      );
      setTemplateObjects(res.template_objects);
    } catch {
      // Availability over completeness: the template layer is a convenience
      // beneath the tenant's own objects, and a failure to load it must not
      // take down the Designer. An empty layer renders no group at all.
      setTemplateObjects([]);
    }
  }, []);

  useEffect(() => {
    void loadTemplateObjects();
  }, [loadTemplateObjects]);

  /**
   * Template rows with their state resolved against the model currently open.
   * Shadowing is client-side because it depends on which model is open — the
   * hub cannot know that.
   */
  const templateRows = useMemo<TemplateRow[]>(() => {
    if (!spec) return [];
    const excluded = new Set(
      templateObjects.filter((t) => t.excluded).map((t) => t.name.toLowerCase())
    );
    return buildTemplateRows(
      templateObjects.map((t) => ({ name: t.name, template_kind: t.template_kind })),
      spec.objects.map((o) => ({ id: o.id, name: o.name })),
      excluded
    );
  }, [templateObjects, spec]);

  // Search filtering happens at the render site, beside the other catalog
  // groups — `catalogSearchLower` is declared further down with them.
  const visibleTemplateGroup = useMemo(() => visibleTemplateRows(templateRows), [templateRows]);

  const selectedTemplate = useMemo(
    () => templateObjects.find((t) => t.name.toLowerCase() === (selectedTemplateName ?? "").toLowerCase()) ?? null,
    [templateObjects, selectedTemplateName]
  );

  /**
   * Copy into model — the EXPLICIT fork gesture (§5.4.5).
   *
   * Template objects are read-only everywhere, including no Infer schema: the
   * per-object infer flow rewrites `fields` AND silently converts a `view`
   * source to a `query` source, and a lossy fork is never offered implicitly.
   * This is the deliberate, visible alternative.
   *
   * The clone keeps the name VERBATIM. That is what makes it shadow the
   * template object — shadowing keys on the name, so a "(copy)" suffix would
   * leave the template version serving alongside the copy.
   */
  const copyTemplateIntoModel = useCallback(
    (row: TemplateRow) => {
      const source = templateObjects.find((t) => t.name === row.name);
      if (!source || !spec) return;
      const oid = crypto.randomUUID();
      setSpec((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev)) as SemanticSpecV1;
        const clone = JSON.parse(JSON.stringify(source.object)) as SemanticObject;
        clone.id = oid;
        clone.name = copyIntoModelName(source.name);
        // Fields get fresh ids too: field ids are referenced by relationship
        // `field_mapping` joins, and reusing the template's would let a tenant
        // edit appear to mutate the served template.
        // ADR-0212: a template written before allow / restrict may carry an old class; the copy maps it
        // (§6.1) so the model's next save is not refused for a value nobody here chose.
        clone.fields = (clone.fields ?? []).map((f) => ({
          ...f,
          id: crypto.randomUUID(),
          security_profile: { ...f.security_profile, security_class: legacyAccessValue(f.security_profile?.security_class) }
        }));
        next.objects.push(clone);
        const cur = next.subject_areas.find((sa) => sa.id === activeSaId);
        const master = next.subject_areas.find((sa) => sa.is_master);
        const place = { object_id: oid, ui: { x: 120 + Math.random() * 80, y: 120 + Math.random() * 80 } };
        if (cur) cur.object_placements.push({ ...place });
        if (master && cur && master.id !== cur.id) {
          master.object_placements.push({ object_id: oid, ui: { x: 0, y: 0 } });
        }
        return next;
      });
      setSelectedTemplateName(null);
      setInspectorTopTab("config");
      setSelectedFieldId(null);
      setSelectedRelationshipId(null);
      setSelectedObjectId(oid);
      setObjectConfigSubTab("metadata");
      toast.push({ kind: "success", message: "Copied — this model now shadows the template object." });
    },
    [templateObjects, spec, activeSaId, toast]
  );

  const excludeTemplateObject = useCallback(
    async (row: TemplateRow) => {
      const copy = excludeConfirmCopy(row.name);
      const ok = await confirm.confirm({
        title: copy.title,
        detail: copy.detail,
        confirmLabel: copy.confirmLabel
      });
      if (!ok) return;
      setTemplateBusy(row.name);
      try {
        await apiFetch("/api/tenant/semantic-template-exclusions", {
          method: "POST",
          body: { kind: row.template_kind, object_name: row.name }
        });
        await loadTemplateObjects();
        toast.push({ kind: "success", message: `"${row.name}" excluded. Queries will no longer see it.` });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setTemplateBusy(null);
      }
    },
    [confirm, loadTemplateObjects, toast]
  );

  const restoreTemplateObject = useCallback(
    async (row: TemplateRow) => {
      setTemplateBusy(row.name);
      try {
        const qs = new URLSearchParams({ kind: row.template_kind, object_name: row.name });
        await apiFetch(`/api/tenant/semantic-template-exclusions?${qs.toString()}`, { method: "DELETE" });
        await loadTemplateObjects();
        toast.push({ kind: "success", message: `"${row.name}" restored.` });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setTemplateBusy(null);
      }
    },
    [loadTemplateObjects, toast]
  );

  const addObject = useCallback(() => {
    const oid = crypto.randomUUID();
    setSpec((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as SemanticSpecV1;
      const n = next.objects.length + 1;
      const newName = `Object ${n}`;
      next.objects.push({
        id: oid,
        name: newName,
        description: null,
        source: { type: "view", name: "" },
        fields: []
      });
      const master = next.subject_areas.find((s) => s.is_master);
      const cur = next.subject_areas.find((s) => s.id === activeSaId);
      const place = { object_id: oid, ui: { x: 120 + Math.random() * 80, y: 120 + Math.random() * 80 } };
      if (cur) {
        cur.object_placements.push({ ...place });
      }
      if (master && cur && master.id !== cur.id) {
        master.object_placements.push({ object_id: oid, ui: { x: 0, y: 0 } });
      }
      return next;
    });
    setInspectorTopTab("config");
    setSelectedFieldId(null);
    setSelectedRelationshipId(null);
    setSelectedObjectId(oid);
  }, [activeSaId]);

  // ADR-0212 §4.3 — no prompt: the "+" tab creates "New area" at once, makes it the active area and opens
  // its Subject area tab with Name focused; the name autosaves on blur like every other field.
  const addSubjectArea = useCallback(() => {
    const id = crypto.randomUUID();
    setSpec((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev)) as SemanticSpecV1;
      next.subject_areas.push({
        id,
        name: "New area",
        description: null,
        is_master: false,
        display_mode: "columns",
        object_placements: [],
        ui: null
      });
      return next;
    });
    setActiveSaId(id);
    setSelectedObjectId(null);
    setSelectedRelationshipId(null);
    setSelectedFieldId(null);
    setSelectedTemplateName(null);
    setInspectorCollapsed(false);
    setInspectorTopTab("config");
    setModelConfigSubTab("subjectArea");
    setFocusSaNameFor(id);
  }, []);

  const deleteSubjectArea = useCallback(
    async (saId: string) => {
      const sa = spec?.subject_areas.find((s) => s.id === saId);
      if (!sa || sa.is_master) return;
      // ADR-0212 §4.3: the people who hold it are read fresh, named in the confirmation, and lose it with
      // the area (the hub clears their assignments in the same write). Held by anyone → always confirmed.
      const fresh = await loadAreaAssignees();
      if (fresh === null) {
        toast.push({ kind: "error", message: "Could not check who is assigned this area, so it was not deleted. Try again." });
        return;
      }
      const losing = areaDeletionWarning(fresh.get(saId) ?? []);
      if (sa.object_placements.length > 0 || losing) {
        const ok = await confirm.confirm({
          title: "Delete subject area?",
          detail: `This permanently deletes the "${sa.name}" subject area and its canvas layout. The objects themselves stay in the model. This cannot be undone.${losing ? ` ${losing}` : ""}`,
          confirmLabel: "Delete subject area",
          destructive: true,
          requireTypedConfirmation: {
            value: sa.name,
            label: "Type the subject area name to confirm:"
          }
        });
        if (!ok) return;
      }
      setSpec((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev)) as SemanticSpecV1;
        next.subject_areas = next.subject_areas.filter((s) => s.id !== saId);
        return next;
      });
      if (activeSaId === saId) {
        const master = spec?.subject_areas.find((s) => s.is_master);
        setActiveSaId(master?.id ?? "");
      }
      setAreaAssignees((prev) => {
        if (!prev) return prev;
        const next = new Map(prev);
        next.delete(saId);
        return next;
      });
    },
    [spec, activeSaId, confirm, loadAreaAssignees, toast]
  );

  /** ADR-0212 §4.6 — "+ Note": an empty note at the centre of what is visible, selected, its Text focused. */
  const addNote = useCallback(() => {
    if (!spec || !activeSaId) return;
    const box = flowHostRef.current?.getBoundingClientRect();
    const centre =
      box && flowInstanceRef.current
        ? flowInstanceRef.current.screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 2 })
        : { x: 120, y: 120 };
    const id = crypto.randomUUID();
    const next = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const sa = next.subject_areas.find((s) => s.id === activeSaId);
    if (!sa) return;
    sa.notes = [...(sa.notes ?? []), { id, text: "", ui: { x: Math.round(centre.x), y: Math.round(centre.y) } }];
    setSpec(next);
    setSelectedObjectId(null);
    setSelectedRelationshipId(null);
    setSelectedFieldId(null);
    setSelectedTemplateName(null);
    setInspectorCollapsed(false);
    setInspectorTopTab("config");
    setSelectedNoteId(id);
    setFocusNoteFor(id);
  }, [spec, activeSaId]);

  const runLayout = useCallback(() => {
    if (!spec) return;
    const { nodes: n, edges: e } = buildFlowFromSpec(spec, activeSaId);
    setSpec(applyAutoLayout(spec, activeSaId, n, e));
  }, [spec, activeSaId]);

  const persistIfModelNameChanged = useCallback(() => {
    const trimmed = modelName.trim();
    if (lastSavedRef.current && trimmed !== lastSavedRef.current.name) {
      void persistNow(undefined);
    }
  }, [modelName, persistNow]);

  const persistIfDescriptionChanged = useCallback(() => {
    const trimmed = modelDescription.trim();
    const lastDesc = lastSavedRef.current?.description ?? "";
    if (lastSavedRef.current && trimmed !== lastDesc) {
      void persistNow(undefined);
    }
  }, [modelDescription, persistNow]);

  const commitObjectNameOnBlur = useCallback(() => {
    if (!selectedObjectId || !spec) return;
    const o = spec.objects.find((x) => x.id === selectedObjectId);
    if (!o) return;
    const nextName = objectNameDraft.trim();
    if (nextName === o.name || !nextName) {
      setObjectNameDraft(o.name);
      return;
    }
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const ob = copy.objects.find((x) => x.id === selectedObjectId);
    if (ob) ob.name = nextName;
    setSpec(copy);
    clearSpecDebounce();
    void persistNow(copy);
  }, [selectedObjectId, spec, objectNameDraft, persistNow, clearSpecDebounce]);

  const commitObjectDescOnBlur = useCallback(() => {
    if (!selectedObjectId || !spec) return;
    const o = spec.objects.find((x) => x.id === selectedObjectId);
    if (!o) return;
    const nextDesc = objectDescDraft.trim() || null;
    const prevDesc = o.description ?? null;
    if (nextDesc === prevDesc) return;
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const ob = copy.objects.find((x) => x.id === selectedObjectId);
    if (ob) ob.description = nextDesc;
    setSpec(copy);
    clearSpecDebounce();
    void persistNow(copy);
  }, [selectedObjectId, spec, objectDescDraft, persistNow, clearSpecDebounce]);

  const commitObjectSourceOnBlur = useCallback(() => {
    if (!selectedObjectId || !spec) return;
    const o = spec.objects.find((x) => x.id === selectedObjectId);
    if (!o) return;
    const t = sourceTypeDraft.trim() || "view";
    const src = o.source as Record<string, unknown>;
    let nextSource: Record<string, unknown>;
    if (t === "query") {
      const sql = sourceNameDraft.trim();
      const connectorId = sourceConnectorDraft.trim();
      if (
        String(src.type ?? "") === t &&
        String(src.sql ?? "") === sql &&
        String(src.connector_id ?? "") === connectorId
      ) {
        return;
      }
      nextSource = { type: t, sql, connector_id: connectorId };
    } else {
      const n = sourceNameDraft.trim();
      if (String(src.type ?? "") === t && String(src.name ?? "") === n) return;
      nextSource = { type: t, name: n };
    }
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const ob = copy.objects.find((x) => x.id === selectedObjectId);
    if (ob) ob.source = nextSource;
    setSpec(copy);
    clearSpecDebounce();
    void persistNow(copy);
  }, [
    selectedObjectId,
    spec,
    sourceTypeDraft,
    sourceNameDraft,
    sourceConnectorDraft,
    persistNow,
    clearSpecDebounce
  ]);

  const addColumnToObject = useCallback(() => {
    if (!selectedObjectId || !spec) return;
    const fid = crypto.randomUUID();
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const o = copy.objects.find((x) => x.id === selectedObjectId);
    if (!o) return;
    o.fields.push({
      id: fid,
      name: `field_${o.fields.length + 1}`,
      data_type: "varchar",
      primary_key: false,
      security_profile: { security_class: "allow", exposure_mode: "full" }
    });
    setSpec(copy);
    setObjectConfigSubTab("fields");
    clearSpecDebounce();
    void persistNow(copy);
  }, [selectedObjectId, spec, persistNow, clearSpecDebounce]);

  const removeFieldFromObject = useCallback(
    (objectId: string, fieldId: string) => {
      if (!spec) return;
      const o = spec.objects.find((x) => x.id === objectId);
      if (!o || !o.fields.some((f) => f.id === fieldId)) return;
      // Joins reference fields by id. Take the rows that reference this field
      // with it, the way deleting an object takes its relationships — a row
      // left behind would point at nothing and read in the inspector as the
      // first column (FIXES.md 2026-10-06).
      const { spec: copy, removedRows, removedRelationships } = removeFieldFromSpec(spec, objectId, fieldId);
      setSpec(copy);
      clearSpecDebounce();
      void persistNow(copy);
      if (removedRows > 0) {
        const joins =
          removedRelationships.length > 0
            ? `; removed ${removedRelationships.length === 1 ? "join" : "joins"} ${removedRelationships.join(", ")}`
            : "";
        toast.push({
          kind: "info",
          message: `Removed ${removedRows} join ${removedRows === 1 ? "mapping" : "mappings"} that used this column${joins}.`
        });
      }
    },
    [spec, clearSpecDebounce, persistNow, toast]
  );

  const moveFieldInObject = useCallback(
    (objectId: string, fieldId: string, dir: "up" | "down") => {
      if (!spec) return;
      const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
      const o = copy.objects.find((x) => x.id === objectId);
      if (!o) return;
      const i = o.fields.findIndex((f) => f.id === fieldId);
      if (i < 0) return;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= o.fields.length) return;
      const a = o.fields[i]!;
      const b = o.fields[j]!;
      o.fields[i] = b;
      o.fields[j] = a;
      setSpec(copy);
      clearSpecDebounce();
      void persistNow(copy);
    },
    [spec, clearSpecDebounce, persistNow]
  );

  const inferSchemaFromQuery = useCallback(async () => {
    if (!selectedObjectId || !spec) return;
    const sql = sourceNameDraft.trim();
    const connectorId = sourceConnectorDraft.trim();
    if (!connectorId || !sql) {
      toast.push({ kind: "error", message: "Choose a connector and enter SQL before inferring schema." });
      return;
    }
    setInferLoading(true);
    try {
      const data = await apiFetch<{ columns: Array<{ name: string; type: string }> }>(
        "/api/tenant/semantic-models/infer-schema",
        { method: "POST", body: { connector_id: connectorId, sql } }
      );
      const cols = data.columns ?? [];
      const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
      const ob = copy.objects.find((x) => x.id === selectedObjectId);
      if (!ob) return;
      ob.source = { type: "query", sql, connector_id: connectorId };
      ob.fields = mergeInferredFields(ob.fields, cols);
      // ADR-0212: a column the query no longer returns takes its subject-area restrictions with it.
      const { spec: inferred } = pruneRestrictionsToFields(copy);
      setSpec(inferred);
      setSourceTypeDraft("query");
      setSelectedFieldId(null);
      clearSpecDebounce();
      void persistNow(inferred);
      toast.push({ kind: "success", message: cols.length ? `Inferred ${cols.length} column(s).` : "Query returned no columns." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setInferLoading(false);
    }
  }, [
    selectedObjectId,
    spec,
    sourceNameDraft,
    sourceConnectorDraft,
    toast,
    clearSpecDebounce,
    persistNow
  ]);

  const applyFieldColumnPatch = useCallback(
    (objectId: string, fieldId: string, patch: ColumnFieldPatch) => {
      if (!spec) return;
      const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
      const o = copy.objects.find((x) => x.id === objectId);
      const fl = o?.fields.find((x) => x.id === fieldId);
      if (!fl) return;
      let changed = false;
      if (patch.name !== undefined) {
        const n = patch.name.trim();
        if (!n) return;
        if (fl.name !== n) {
          fl.name = n;
          changed = true;
        }
      }
      if (patch.data_type !== undefined) {
        const raw = patch.data_type.trim();
        const t = SEMANTIC_FIELD_DATA_TYPE_SET.has(raw) ? raw : "varchar";
        if (fl.data_type !== t) {
          fl.data_type = t;
          changed = true;
        }
      }
      if (patch.primary_key !== undefined && fl.primary_key !== patch.primary_key) {
        fl.primary_key = patch.primary_key;
        changed = true;
      }
      if (patch.security_class !== undefined) {
        const s = legacyAccessValue(patch.security_class);
        if (fl.security_profile.security_class !== s) {
          fl.security_profile = { ...fl.security_profile, security_class: s };
          changed = true;
        }
      }
      if (patch.exposure_mode !== undefined) {
        const em = patch.exposure_mode.trim() || "full";
        if (fl.security_profile.exposure_mode !== em) {
          fl.security_profile = { ...fl.security_profile, exposure_mode: em };
          changed = true;
        }
      }
      if (patch.description !== undefined) {
        const d = patch.description === null ? null : String(patch.description);
        if ((fl.description ?? null) !== d) {
          fl.description = d;
          changed = true;
        }
      }
      if (patch.output_format !== undefined) {
        const o = patch.output_format === null ? null : String(patch.output_format);
        if ((fl.output_format ?? null) !== o) {
          fl.output_format = o;
          changed = true;
        }
      }
      if (patch.notes !== undefined) {
        const n = patch.notes === null ? null : String(patch.notes);
        if ((fl.notes ?? null) !== n) {
          fl.notes = n;
          changed = true;
        }
      }
      if (!changed) return;
      setSpec(copy);
      clearSpecDebounce();
      void persistNow(copy);
    },
    [spec, clearSpecDebounce, persistNow]
  );

  const onStatusChange = useCallback(
    (next: SemanticModelStatus) => {
      setRowStatus(next);
      rowStatusRef.current = next;
      clearSpecDebounce();
      void persistNow(undefined);
    },
    [persistNow, clearSpecDebounce]
  );

  const placedOnActiveSa = useMemo(() => {
    if (!spec) return new Set<string>();
    const sa = spec.subject_areas.find((s) => s.id === activeSaId);
    return new Set((sa?.object_placements ?? []).map((p) => p.object_id));
  }, [spec, activeSaId]);

  const catalogSearchLower = catalogSearch.trim().toLowerCase();

  const catalogPlacedObjects = useMemo(() => {
    if (!spec) return [];
    const byId = new Map(spec.objects.map((o) => [o.id, o] as const));
    const sa = spec.subject_areas.find((s) => s.id === activeSaId);
    const ids = (sa?.object_placements ?? []).map((p) => p.object_id);
    return ids
      .map((id) => byId.get(id))
      .filter((o): o is SemanticObject => !!o)
      .filter((o) => !catalogSearchLower || o.name.toLowerCase().includes(catalogSearchLower))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [spec, activeSaId, catalogSearchLower]);

  const catalogUnplacedObjects = useMemo(() => {
    if (!spec) return [];
    return [...spec.objects]
      .filter((o) => !placedOnActiveSa.has(o.id))
      .filter((o) => !catalogSearchLower || o.name.toLowerCase().includes(catalogSearchLower))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [spec, placedOnActiveSa, catalogSearchLower]);

  const addFromMasterCandidates = useMemo(() => {
    if (!spec || isMasterActive) return [];
    const master = spec.subject_areas.find((s) => s.is_master);
    const cur = spec.subject_areas.find((s) => s.id === activeSaId);
    if (!master || !cur) return [];
    const activeIds = new Set(cur.object_placements.map((p) => p.object_id));
    const masterOnly = master.object_placements.map((p) => p.object_id).filter((id) => !activeIds.has(id));
    const byId = new Map(spec.objects.map((o) => [o.id, o] as const));
    return masterOnly.map((id) => byId.get(id)).filter((o): o is SemanticObject => !!o);
  }, [spec, activeSaId, isMasterActive]);

  const centerOnPlacedObject = useCallback(
    (objectId: string) => {
      const rf = flowInstanceRef.current;
      const s = specRef.current;
      if (!rf || !s) return;
      const node = rf.getNode(objectId);
      if (!node) return;
      const obj = s.objects.find((o) => o.id === objectId);
      const sa = s.subject_areas.find((x) => x.id === activeSaId);
      const dm = normalizeDisplayMode(sa?.display_mode);
      const h = estimateSemanticNodeHeight(dm, obj);
      const w = SEMANTIC_NODE_LAYOUT_WIDTH;
      void rf.setCenter(node.position.x + w / 2, node.position.y + h / 2, { zoom: rf.getZoom(), duration: 220 });
    },
    [activeSaId]
  );

  useEffect(() => {
    setTagsInput(modelTags.join(", "));
  }, [modelTags]);

  useEffect(() => {
    if (!activeSa) {
      setSaNameDraft("");
      setSaDescDraft("");
      return;
    }
    setSaNameDraft(activeSa.name);
    setSaDescDraft(activeSa.description ?? "");
  }, [activeSaId, activeSa?.name, activeSa?.description]);

  useEffect(() => {
    // Wait until the field shows the new area's name: selecting earlier, the draft's own update then moves
    // the caret to the end and the first keystrokes append to "New area" instead of replacing it.
    if (!focusSaNameFor || activeSa?.id !== focusSaNameFor || saNameDraft !== activeSa.name || !saNameInputRef.current) return;
    saNameInputRef.current.focus();
    saNameInputRef.current.select();
    setFocusSaNameFor(null);
  }, [focusSaNameFor, activeSa?.id, activeSa?.name, modelConfigSubTab, saNameDraft]);

  const selectedField =
    selectedObject && selectedFieldId ? selectedObject.fields.find((f) => f.id === selectedFieldId) ?? null : null;

  useEffect(() => {
    if (!selectedField) return;
    setFieldDetailNameDraft(selectedField.name);
    setFieldDetailDescDraft(selectedField.description ?? "");
    setFieldDetailOutputDraft(selectedField.output_format ?? "");
    setFieldDetailNotesDraft(selectedField.notes ?? "");
    setFieldDetailSecurityClassDraft(legacyAccessValue(selectedField.security_profile?.security_class));
    setFieldDetailExposureDraft(selectedField.security_profile.exposure_mode);
    setFieldDetailTypeDraft(selectedField.data_type);
  }, [selectedFieldId, selectedField]);

  const subjectAreasContainingObject = useMemo(() => {
    if (!spec || !selectedObjectId) return [];
    return spec.subject_areas
      .filter((sa) => sa.object_placements.some((p) => p.object_id === selectedObjectId))
      .map((sa) => sa.name);
  }, [spec, selectedObjectId]);

  /**
   * ADR-0212 §4.3 (C2) — the active area, when it can restrict the selected object's columns: a non-Master
   * area that places it. Master holds every object and restricts nothing, so it shows no "In ‹area›" column.
   */
  const restrictionArea =
    activeSa && !activeSa.is_master && selectedObjectId && activeSa.object_placements.some((p) => p.object_id === selectedObjectId)
      ? activeSa
      : null;

  const setFieldRestrictedInArea = useCallback(
    (objectId: string, fieldId: string, restricted: boolean) => {
      if (!spec) return;
      const next = setAreaRestriction(spec, activeSaId, objectId, fieldId, restricted);
      if (!next) return;
      setSpec(next);
      clearSpecDebounce();
      void persistNow(next);
    },
    [spec, activeSaId, clearSpecDebounce, persistNow]
  );

  const selectedNote = (selectedNoteId && activeSa?.notes?.find((n) => n.id === selectedNoteId)) || null;

  useEffect(() => {
    setNoteTextDraft(selectedNote?.text ?? "");
  }, [selectedNoteId, selectedNote?.text]);

  useEffect(() => {
    if (!focusNoteFor || selectedNote?.id !== focusNoteFor || !noteTextRef.current) return;
    noteTextRef.current.focus();
    setFocusNoteFor(null);
  }, [focusNoteFor, selectedNote?.id]);

  const commitNoteOnBlur = useCallback(() => {
    if (!spec || !selectedNote) return;
    const text = noteTextDraft.slice(0, NOTE_MAX_CHARS);
    if (text === selectedNote.text) return;
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const note = copy.subject_areas.find((s) => s.id === activeSaId)?.notes?.find((n) => n.id === selectedNote.id);
    if (!note) return;
    note.text = text;
    setSpec(copy);
    clearSpecDebounce();
    void persistNow(copy);
  }, [spec, selectedNote, noteTextDraft, activeSaId, clearSpecDebounce, persistNow]);

  const commitSubjectAreaOnBlur = useCallback(() => {
    if (!spec || !activeSa || activeSa.is_master) return;
    const nextName = saNameDraft.trim();
    const nextDesc = saDescDraft.trim() || null;
    if (!nextName) {
      setSaNameDraft(activeSa.name);
      return;
    }
    if (nextName === activeSa.name && nextDesc === (activeSa.description ?? null)) return;
    const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
    const sa = copy.subject_areas.find((s) => s.id === activeSaId);
    if (!sa) return;
    sa.name = nextName;
    sa.description = nextDesc;
    setSpec(copy);
    clearSpecDebounce();
    void persistNow(copy);
  }, [spec, activeSa, activeSaId, saNameDraft, saDescDraft, clearSpecDebounce, persistNow]);

  const commitTagsOnBlur = useCallback(() => {
    const next = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (JSON.stringify(next) === JSON.stringify(modelTags)) return;
    setModelTags(next);
    modelTagsRef.current = next;
    clearSpecDebounce();
    void persistNow(undefined);
  }, [tagsInput, modelTags, clearSpecDebounce, persistNow]);

  const addPlacementFromMaster = useCallback(
    (objectId: string) => {
      const rf = flowInstanceRef.current;
      const s = specRef.current;
      if (!rf || !s) return;
      const host = document.querySelector(".semanticDesignerFlowHost");
      if (!(host instanceof HTMLElement)) return;
      const rect = host.getBoundingClientRect();
      const pos = rf.screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      const copy = addPlacementOnlyAt(s, activeSaId, objectId, pos);
      if (copy === s) return;
      setSpec(copy);
      setAddFromMasterOpen(false);
      setInspectorTopTab("config");
      setSelectedFieldId(null);
      setSelectedRelationshipId(null);
      setSelectedObjectId(objectId);
      setObjectConfigSubTab("metadata");
      clearSpecDebounce();
      void persistNow(copy);
    },
    [activeSaId, clearSpecDebounce, persistNow]
  );

  const onCatalogDropOnCanvas = useCallback(
    (objectId: string, flowPosition: { x: number; y: number }) => {
      setSpec((prev) => {
        if (!prev) return prev;
        const sa = prev.subject_areas.find((s) => s.id === activeSaId);
        if (sa?.object_placements.some((p) => p.object_id === objectId)) return prev;
        return addPlacementOnlyAt(prev, activeSaId, objectId, flowPosition);
      });
      setInspectorTopTab("config");
      setSelectedFieldId(null);
      setSelectedRelationshipId(null);
      setSelectedObjectId(objectId);
      setObjectConfigSubTab("metadata");
    },
    [activeSaId]
  );

  const onExplorerRowDragStart = useCallback((e: { dataTransfer: DataTransfer | null }, objectId: string) => {
    if (!e.dataTransfer) return;
    e.dataTransfer.setData(SEMANTIC_OBJECT_DRAG_MIME, JSON.stringify({ objectId }));
    e.dataTransfer.effectAllowed = "copy";
  }, []);

  if (loading) {
    return (
      <PageLayout title="Semantic Models">
        <LoadingState />
      </PageLayout>
    );
  }

  if (error || !spec) {
    return (
      <PageLayout title="Semantic Models">
        <ErrorState detail={error ?? "Missing spec"} />
        <button className="btn" type="button" onClick={() => nav("/app/build/semantic-models")}>
          Back to list
        </button>
      </PageLayout>
    );
  }

  const selectedRelationship = spec?.relationships.find((r) => r.id === selectedRelationshipId) ?? null;

  const inspectorHeaderTitle =
    inspectorTopTab === "catalog"
      ? "Catalog"
      : selectedRelationship
        ? `Relationship · ${selectedRelationship.name}`
        : selectedField && selectedObject
          ? `Field · ${selectedField.name}`
          : selectedObject
            ? `Object · ${selectedObject.name}`
            : "Config";

  const commitFieldDetailOnBlur = () => {
    if (!selectedObjectId || !selectedFieldId || !spec || !selectedField || !selectedObject) return;
    const q = String(selectedObject.source?.type ?? "") === "query";
    const patch: ColumnFieldPatch = {};
    if (!q && fieldDetailNameDraft.trim() && fieldDetailNameDraft.trim() !== selectedField.name) {
      patch.name = fieldDetailNameDraft.trim();
    }
    if (fieldDetailTypeDraft !== selectedField.data_type) patch.data_type = fieldDetailTypeDraft;
    if (fieldDetailDescDraft !== (selectedField.description ?? "")) patch.description = fieldDetailDescDraft.trim() || null;
    if (fieldDetailOutputDraft !== (selectedField.output_format ?? "")) patch.output_format = fieldDetailOutputDraft.trim() || null;
    if (fieldDetailNotesDraft !== (selectedField.notes ?? "")) patch.notes = fieldDetailNotesDraft.trim() || null;
    if (fieldDetailSecurityClassDraft !== selectedField.security_profile.security_class) {
      patch.security_class = fieldDetailSecurityClassDraft;
    }
    if (fieldDetailExposureDraft !== selectedField.security_profile.exposure_mode) {
      patch.exposure_mode = fieldDetailExposureDraft;
    }
    if (Object.keys(patch).length === 0) return;
    applyFieldColumnPatch(selectedObjectId, selectedFieldId, patch);
  };

  return (
    <PageLayout
      title={<><Link to="/app/build/semantic-models">Semantic Models</Link> / {modelName || "Untitled"}</>}
      actions={
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" className="btn btnLink semanticDesignerSaveFallback" onClick={() => void persistNow(undefined)} disabled={saving}>
            Save now
          </button>
          <span className="muted semanticDesignerSaveFallbackHint" title="Changes usually save automatically (⌘S / Ctrl+S)">
            {saving ? "Saving…" : ""}
          </span>
          <button className="btn btnSm" type="button" onClick={() => nav("/app/build/semantic-models")}>
            Close
          </button>
        </div>
      }
    >
      <ReactFlowProvider>
        <div className="semanticDesignerWorkspace">
          <section
            ref={semInspectorAreaRef}
            className="card reportEditorLeft semanticDesignerCenter"
            style={{ flex: 1, minWidth: 0, alignContent: "start" }}
            onMouseDownCapture={(e) => {
              const el = e.target as HTMLElement;
              if (el.closest(".react-flow") || el.closest("button")?.getAttribute("role") === "tab") return;
              if (el.closest(".queryTabClose")) return;
              setInspectorTopTab("config");
              setSelectedObjectId(null);
              setSelectedRelationshipId(null);
              setSelectedFieldId(null);
            }}
          >
          <div className="queryTabBar" role="tablist" aria-label="Subject areas">
            {spec.subject_areas.map((sa) => {
              const active = sa.id === activeSaId;
              return (
                <button
                  key={sa.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`queryTab${active ? " queryTabActive" : ""}`}
                  onClick={() => {
                    setActiveSaId(sa.id);
                    setInspectorTopTab("config");
                    setSelectedObjectId(null);
                    setSelectedRelationshipId(null);
                    setSelectedFieldId(null);
                  }}
                >
                  <span className="queryTabLabel">
                    {sa.is_master ? "★ " : ""}
                    {sa.name}
                  </span>
                  {!sa.is_master ? (
                    <span
                      role="button"
                      tabIndex={0}
                      className="queryTabClose"
                      aria-label={`Delete ${sa.name}`}
                      title={`Delete subject area "${sa.name}"`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void deleteSubjectArea(sa.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                          void deleteSubjectArea(sa.id);
                        }
                      }}
                    >
                      ×
                    </span>
                  ) : null}
                </button>
              );
            })}
            <button type="button" className="queryTabAdd" aria-label="Add subject area" onClick={() => addSubjectArea()}>
              +
            </button>
          </div>

          <div className="reportSection" style={{ marginTop: 12, marginBottom: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
              <button className="btn btnSm" type="button" onClick={() => addObject()}>
                + Object
              </button>
              <button className="btn btnSm" type="button" onClick={() => setImportWizardOpen(true)}>
                Import
              </button>
              <button className="btn btnSm" type="button" onClick={() => runLayout()}>
                Auto layout
              </button>
              <button className="btn btnSm" type="button" onClick={() => addNote()}>
                + Note
              </button>
            </div>
          </div>

          <FlowCanvasWithDrop
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={handleEdgesChange}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onNodeDragStop={onNodeDragStop}
            onNodesDelete={(d) => void onNodesDelete(d)}
            hostRef={flowHostRef}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onCatalogDropOnCanvas={onCatalogDropOnCanvas}
            flowInstanceRef={flowInstanceRef}
          />
        </section>

        <InspectorFlyout
          surfaceId="semanticDesigner"
          panelId="inspector"
          open={!inspectorCollapsed}
          onOpenChange={(o) => setInspectorCollapsed(!o)}
          stripLabel="Inspector"
          topOffset={semInspectorFlyoutTop}
          widthConfig={{ min: 360, max: (vw) => Math.min(vw - 80, 1100), default: 480 }}
          headerNav={inspectorHeaderTitle}
        >
          <div className="operationNodeInspectorBody">
          <>
            <div className="tabs" role="tablist" aria-label="Inspector">
              <button
                type="button"
                role="tab"
                aria-selected={inspectorTopTab === "catalog"}
                className={`tab${inspectorTopTab === "catalog" ? " tabActive" : ""}`}
                onClick={() => setInspectorTopTab("catalog")}
              >
                Catalog
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={inspectorTopTab === "config"}
                className={`tab${inspectorTopTab === "config" ? " tabActive" : ""}`}
                onClick={() => setInspectorTopTab("config")}
              >
                Config
              </button>
            </div>
            <div className="tabPanel" style={{ marginTop: 10 }}>
              {inspectorTopTab === "catalog" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="label" htmlFor="sem-catalog-search">
                      Search
                    </label>
                    <input
                      id="sem-catalog-search"
                      className="input"
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      placeholder="Filter by name…"
                      autoComplete="off"
                    />
                  </div>
                  {!isMasterActive ? (
                    <button type="button" className="btn btnSm" onClick={() => setAddFromMasterOpen((o) => !o)}>
                      {addFromMasterOpen ? "Hide Add from Master" : "+ Add from Master"}
                    </button>
                  ) : null}
                  {addFromMasterOpen && !isMasterActive ? (
                    <div
                      className="card"
                      style={{ padding: 8, maxHeight: 160, overflow: "auto", border: "1px solid var(--line, #e2e8f0)" }}
                    >
                      {addFromMasterCandidates.length === 0 ? (
                        <span className="muted" style={{ fontSize: 12 }}>
                          No objects in Master missing from this subject area.
                        </span>
                      ) : (
                        <ul className="semanticObjectExplorerList" role="list" style={{ margin: 0 }}>
                          {addFromMasterCandidates.map((o) => (
                            <li key={o.id} className="semanticObjectExplorerItem">
                              <button
                                type="button"
                                className="semanticObjectExplorerRow"
                                onClick={() => addPlacementFromMaster(o.id)}
                              >
                                <span className="semanticObjectExplorerRowName">{o.name}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                  <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                    On canvas ({catalogPlacedObjects.length})
                  </div>
                  <ul className="semanticObjectExplorerList" role="list" style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                    {catalogPlacedObjects.map((o) => {
                      const hasPk = o.fields.some((f) => f.primary_key);
                      return (
                        <li key={o.id} className="semanticObjectExplorerItem">
                          <button
                            type="button"
                            className={`semanticObjectExplorerRow semanticObjectExplorerRowPlaced${selectedObjectId === o.id ? " semanticObjectExplorerRowSelected" : ""}`}
                            onClick={() => {
                              setInspectorTopTab("config");
                              setSelectedRelationshipId(null);
                              setSelectedFieldId(null);
                              setSelectedObjectId(o.id);
                              setObjectConfigSubTab("metadata");
                              centerOnPlacedObject(o.id);
                            }}
                          >
                            <span className="semanticObjectExplorerRowName" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span aria-hidden>{hasPk ? "★" : "⚠"}</span>
                              <span>
                                {o.name}{" "}
                                <span className="muted" style={{ fontWeight: 400 }}>
                                  ({o.fields.length})
                                </span>
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                    Not on canvas — drag to add
                  </div>
                  <ul className="semanticObjectExplorerList" role="list">
                    {catalogUnplacedObjects.map((o) => (
                      <li key={o.id} className="semanticObjectExplorerItem">
                        <button
                          type="button"
                          draggable
                          onDragStart={(e) => onExplorerRowDragStart(e, o.id)}
                          onClick={() => {
                            setInspectorTopTab("config");
                            setSelectedRelationshipId(null);
                            setSelectedFieldId(null);
                            setSelectedObjectId(o.id);
                            setObjectConfigSubTab("metadata");
                          }}
                          className={`semanticObjectExplorerRow${selectedObjectId === o.id ? " semanticObjectExplorerRowSelected" : ""}`}
                        >
                          <span className="semanticObjectExplorerRowName" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span aria-hidden>{o.fields.some((f) => f.primary_key) ? "★" : "⚠"}</span>
                            <span>
                              {o.name}{" "}
                              <span className="muted" style={{ fontWeight: 400 }}>
                                ({o.fields.length})
                              </span>
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>

                  {/*
                    ADR-0161 §7.2 — the served template layer. Rendered only when
                    the tenant has a connector kind that ships a template; no
                    group at all otherwise, and no "you have no templates" hedge.

                    Rows are NOT draggable: a template object has no placement
                    and cannot be put on the canvas. Shadowed rows are absent
                    entirely — the tenant object of that name carries the
                    disclosure instead (see its Metadata tab).
                  */}
                  {shouldShowTemplateGroup(templateObjects) && visibleTemplateGroup.length > 0 ? (
                    <>
                      <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                        Template — standard objects, served
                      </div>
                      <div className="help" style={{ marginBottom: 6 }}>
                        Standard objects for this tenant&rsquo;s connector. Queries can already use them. They are
                        read-only here — copy one into this model to change it.
                      </div>
                      <ul className="semanticObjectExplorerList" role="list">
                        {visibleTemplateGroup
                          .filter((r) => !catalogSearchLower || r.name.toLowerCase().includes(catalogSearchLower))
                          .map((row) => {
                            const actions = templateRowActions(row.state);
                            const busy = templateBusy === row.name;
                            const isExcluded = row.state === "excluded";
                            return (
                              <li key={`tpl-${row.template_kind}-${row.name}`} className="semanticObjectExplorerItem">
                                <button
                                  type="button"
                                  className={`semanticObjectExplorerRow${
                                    selectedTemplateName === row.name ? " semanticObjectExplorerRowSelected" : ""
                                  }`}
                                  onClick={() => {
                                    setInspectorTopTab("config");
                                    setSelectedObjectId(null);
                                    setSelectedRelationshipId(null);
                                    setSelectedFieldId(null);
                                    setSelectedTemplateName(row.name);
                                  }}
                                >
                                  <span
                                    className="semanticObjectExplorerRowName"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      opacity: isExcluded ? 0.55 : 1
                                    }}
                                  >
                                    <span>{row.name}</span>
                                    <span className={isExcluded ? "pill pillWarn" : "pill pillNeutral"}>
                                      {isExcluded ? "Excluded" : "Template"}
                                    </span>
                                  </span>
                                </button>
                                <div className="actions" style={{ gap: 6, justifyContent: "flex-end" }}>
                                  {actions.includes("copy") ? (
                                    <button
                                      type="button"
                                      className="adminListAction"
                                      disabled={busy}
                                      onClick={() => copyTemplateIntoModel(row)}
                                    >
                                      Copy into model
                                    </button>
                                  ) : null}
                                  {actions.includes("exclude") ? (
                                    <button
                                      type="button"
                                      className="adminListAction isDanger"
                                      disabled={busy}
                                      onClick={() => void excludeTemplateObject(row)}
                                    >
                                      Exclude
                                    </button>
                                  ) : null}
                                  {actions.includes("restore") ? (
                                    <button
                                      type="button"
                                      className="adminListAction"
                                      disabled={busy}
                                      onClick={() => void restoreTemplateObject(row)}
                                    >
                                      Restore
                                    </button>
                                  ) : null}
                                </div>
                              </li>
                            );
                          })}
                      </ul>
                    </>
                  ) : null}
                </div>
              ) : null}

              {/*
                ADR-0161 §7.2 — a selected TEMPLATE object. Read-only throughout:
                every input is `readOnly`/disabled per the §3.2 idiom, and there
                is deliberately no Infer schema action — today's per-object infer
                rewrites `fields` AND silently converts a `view` source to a
                `query` source, and a lossy fork is never offered implicitly.
                "Copy into model" is the explicit alternative.
              */}
              {inspectorTopTab === "config" && selectedTemplate ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>{selectedTemplate.name}</strong>
                    <span className={selectedTemplate.excluded ? "pill pillWarn" : "pill pillNeutral"}>
                      {selectedTemplate.excluded ? "Excluded" : "Template"}
                    </span>
                  </div>
                  <div className="help">
                    {selectedTemplate.excluded
                      ? "Excluded from this tenant. Queries do not see it. Restore it from the Template list."
                      : "A standard object served to every tenant on this connector kind. Read-only — copy it into this model to change it."}
                  </div>

                  <label className="field">
                    <span className="label">Name</span>
                    <input className="input" value={selectedTemplate.name} readOnly />
                  </label>

                  <label className="field">
                    <span className="label">Description</span>
                    <input
                      className="input"
                      value={
                        typeof selectedTemplate.object.description === "string"
                          ? selectedTemplate.object.description
                          : ""
                      }
                      readOnly
                    />
                  </label>

                  <label className="field">
                    <span className="label">Source</span>
                    <input
                      className="input"
                      value={(() => {
                        const src = selectedTemplate.object.source as Record<string, unknown> | null;
                        const type = typeof src?.type === "string" ? src.type : "—";
                        const nm = typeof src?.name === "string" && src.name ? src.name : selectedTemplate.name;
                        return type === "query" ? "query (inline SQL)" : `${type}: ${nm}`;
                      })()}
                      readOnly
                    />
                  </label>

                  <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                    Fields ({selectedTemplate.object.fields?.length ?? 0})
                  </div>
                  <div className="tableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          <th>Key</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedTemplate.object.fields ?? []).map((f) => (
                          <tr key={f.id ?? f.name}>
                            <td className="mono">{f.name}</td>
                            <td className="muted">{f.data_type}</td>
                            <td className="muted">{f.primary_key ? "PK" : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="actions">
                    {selectedTemplate.excluded ? (
                      <button
                        type="button"
                        className="btn"
                        disabled={templateBusy === selectedTemplate.name}
                        onClick={() =>
                          void restoreTemplateObject({
                            name: selectedTemplate.name,
                            template_kind: selectedTemplate.template_kind,
                            state: "excluded"
                          })
                        }
                      >
                        Restore
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btnPrimary"
                          disabled={templateBusy === selectedTemplate.name}
                          onClick={() =>
                            copyTemplateIntoModel({
                              name: selectedTemplate.name,
                              template_kind: selectedTemplate.template_kind,
                              state: "available"
                            })
                          }
                        >
                          Copy into model
                        </button>
                        <button
                          type="button"
                          className="btn btnDanger"
                          disabled={templateBusy === selectedTemplate.name}
                          onClick={() =>
                            void excludeTemplateObject({
                              name: selectedTemplate.name,
                              template_kind: selectedTemplate.template_kind,
                              state: "available"
                            })
                          }
                        >
                          Exclude
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : null}

              {inspectorTopTab === "config" && selectedRelationship ? (
                <RelationshipInspector
                  relationship={selectedRelationship}
                  spec={spec}
                  onApplyPatch={applyRelationshipPatch}
                  onSelectObject={(oid) => {
                    setInspectorTopTab("config");
                    setSelectedObjectId(oid);
                    setSelectedRelationshipId(null);
                    setSelectedFieldId(null);
                    setObjectConfigSubTab("metadata");
                    centerOnPlacedObject(oid);
                  }}
                />
              ) : null}

              {inspectorTopTab === "config" && !selectedRelationship && selectedObject && selectedField ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <button
                    type="button"
                    className="btn btnLink"
                    style={{ padding: 0, justifySelf: "start" }}
                    onClick={() => setSelectedFieldId(null)}
                  >
                    ← Back to object
                  </button>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-name">
                      Name
                    </label>
                    <input
                      id="sem-fld-name"
                      className="input"
                      disabled={String(selectedObject.source?.type ?? "") === "query"}
                      value={fieldDetailNameDraft}
                      onChange={(e) => setFieldDetailNameDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                    />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-type">
                      Data type
                    </label>
                    <select
                      id="sem-fld-type"
                      className="input"
                      value={
                        SEMANTIC_FIELD_DATA_TYPE_SET.has(fieldDetailTypeDraft)
                          ? fieldDetailTypeDraft
                          : SEMANTIC_FIELD_DATA_TYPES[0]
                      }
                      onChange={(e) => setFieldDetailTypeDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                    >
                      {SEMANTIC_FIELD_DATA_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-desc">
                      Description
                    </label>
                    <textarea
                      id="sem-fld-desc"
                      className="input reportTextarea"
                      value={fieldDetailDescDraft}
                      onChange={(e) => setFieldDetailDescDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                    />
                  </div>
                  <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                    Access
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-sec">
                      Access
                    </label>
                    {restrictionArea ? (
                      // ADR-0212 R12 — read-only in an area view; the floor is set in Master (see the column table).
                      <>
                        <div id="sem-fld-sec" className="muted">
                          {floorAccessLabel(selectedField)}
                        </div>
                        <div className="help">{FLOOR_SET_IN_MASTER}</div>
                      </>
                    ) : (
                      <select
                        id="sem-fld-sec"
                        className="input"
                        value={fieldDetailSecurityClassDraft}
                        onChange={(e) => setFieldDetailSecurityClassDraft(e.target.value)}
                        onBlur={() => commitFieldDetailOnBlur()}
                      >
                        {SEMANTIC_SECURITY_CLASSES.map((c) => (
                          <option key={c} value={c}>
                            {SEMANTIC_SECURITY_CLASS_LABEL[c]}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-exp">
                      Exposure mode
                    </label>
                    <select
                      id="sem-fld-exp"
                      className="input"
                      value={fieldDetailExposureDraft}
                      onChange={(e) => setFieldDetailExposureDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                    >
                      {SEMANTIC_EXPOSURE_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-fmt">
                      Output format
                    </label>
                    <input
                      id="sem-fld-fmt"
                      className="input mono"
                      value={fieldDetailOutputDraft}
                      onChange={(e) => setFieldDetailOutputDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                      placeholder="$#,##0.00"
                    />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-fld-notes">
                      Notes
                    </label>
                    <textarea
                      id="sem-fld-notes"
                      className="input reportTextarea"
                      value={fieldDetailNotesDraft}
                      onChange={(e) => setFieldDetailNotesDraft(e.target.value)}
                      onBlur={() => commitFieldDetailOnBlur()}
                    />
                  </div>
                </div>
              ) : null}

              {inspectorTopTab === "config" && !selectedRelationship && selectedObject && !selectedField ? (
                <>
                  <div className="tabs" role="tablist" aria-label="Object config">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={objectConfigSubTab === "metadata"}
                      className={`tab${objectConfigSubTab === "metadata" ? " tabActive" : ""}`}
                      onClick={() => setObjectConfigSubTab("metadata")}
                    >
                      Metadata
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={objectConfigSubTab === "source"}
                      className={`tab${objectConfigSubTab === "source" ? " tabActive" : ""}`}
                      onClick={() => setObjectConfigSubTab("source")}
                    >
                      Source
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={objectConfigSubTab === "fields"}
                      className={`tab${objectConfigSubTab === "fields" ? " tabActive" : ""}`}
                      onClick={() => setObjectConfigSubTab("fields")}
                    >
                      Fields
                    </button>
                  </div>
                  <div className="tabPanel" style={{ marginTop: 8 }}>
                    {objectConfigSubTab === "metadata" ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="field">
                          <label className="label" htmlFor="sem-obj-name">
                            Name
                          </label>
                          <input
                            id="sem-obj-name"
                            key={selectedObjectId ?? "none"}
                            className="input"
                            value={objectNameDraft}
                            onChange={(e) => setObjectNameDraft(e.target.value)}
                            onBlur={() => commitObjectNameOnBlur()}
                          />
                          {/*
                            ADR-0161 §7.2 — the shadow disclosure. The template
                            row for this name is HIDDEN from the Template group
                            (listing it would imply it is available), so this
                            line is the only place the displacement is visible.
                            Without it the shadowing would be silent.
                          */}
                          {shadowIndicatorFor(selectedObject.name, templateObjects) ? (
                            <div className="help">
                              {shadowIndicatorFor(selectedObject.name, templateObjects)}
                            </div>
                          ) : null}
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-obj-desc">
                            Description
                          </label>
                          <textarea
                            id="sem-obj-desc"
                            className="input reportTextarea"
                            value={objectDescDraft}
                            onChange={(e) => setObjectDescDraft(e.target.value)}
                            onBlur={() => commitObjectDescOnBlur()}
                          />
                        </div>
                        <div className="field">
                          <div className="label">Subject areas</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {subjectAreasContainingObject.length ? subjectAreasContainingObject.join(", ") : "—"}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {objectConfigSubTab === "source" ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="field">
                          <label className="label" htmlFor="sem-src-type">
                            Source type
                          </label>
                          <select
                            id="sem-src-type"
                            className="input"
                            value={sourceTypeDraft}
                            onChange={(e) => setSourceTypeDraft(e.target.value)}
                            onBlur={() => commitObjectSourceOnBlur()}
                          >
                            <option value="view">view</option>
                            <option value="api_object">api_object</option>
                            <option value="query">query</option>
                          </select>
                        </div>
                        {(sourceTypeDraft === "view" || sourceTypeDraft === "api_object") && (
                          <div className="field">
                            <label className="label" htmlFor="sem-src-name">
                              Source name
                            </label>
                            <input
                              id="sem-src-name"
                              className="input"
                              value={sourceNameDraft}
                              onChange={(e) => setSourceNameDraft(e.target.value)}
                              onBlur={() => commitObjectSourceOnBlur()}
                              placeholder={selectedObject?.name ?? ""}
                            />
                            <div className="help">
                              {sourceNameDraft.trim() && sourceNameDraft.trim() !== selectedObject?.name
                                ? `This object is an alias — SQL will reference "${sourceNameDraft.trim()}" in the database.`
                                : "Leave blank to use the object name as the database view or table name."}
                            </div>
                          </div>
                        )}
                        {sourceTypeDraft === "query" && (
                          <>
                            <div className="field">
                              <label className="label" htmlFor="sem-src-connector">
                                Connector
                              </label>
                              <select
                                id="sem-src-connector"
                                className="input"
                                value={sourceConnectorDraft}
                                onChange={(e) => setSourceConnectorDraft(e.target.value)}
                                onBlur={() => commitObjectSourceOnBlur()}
                              >
                                <option value="">Select connector</option>
                                {connectors.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label className="label" htmlFor="sem-src-sql">
                                SQL query
                              </label>
                              <div className="input" style={{ padding: 0, overflow: "hidden" }}>
                                <CodeMirror
                                  value={sourceNameDraft}
                                  height="160px"
                                  extensions={sqlEditorExtensions}
                                  placeholder="SELECT ..."
                                  onChange={(val) => setSourceNameDraft(val)}
                                  onBlur={() => commitObjectSourceOnBlur()}
                                  basicSetup={{
                                    lineNumbers: true,
                                    foldGutter: false,
                                    highlightActiveLine: true,
                                    bracketMatching: true,
                                    autocompletion: false
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                              <button
                                type="button"
                                className="btn btnSm"
                                disabled={inferLoading}
                                onClick={() => void inferSchemaFromQuery()}
                              >
                                {inferLoading ? "Inferring…" : "Infer schema"}
                              </button>
                              <span className="muted" style={{ fontSize: 11 }}>
                                Runs a limited query against the connector and merges columns into fields (matching names keep IDs).
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                    {objectConfigSubTab === "fields" ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="reportSectionHeader" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                          <button className="btn btnSm" type="button" onClick={() => addColumnToObject()}>
                            + Add field
                          </button>
                        </div>
                        <div className="tableWrap semanticColumnTableWrap">
                          <table className="table tableCompact semanticColumnTable">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>PK</th>
                                <th>Access</th>
                                {restrictionArea ? <th>In {restrictionArea.name}</th> : null}
                                <th>Exposure</th>
                                <th className="semanticColumnTableThReorder" aria-label="Reorder" />
                                <th className="semanticColumnTableThDelete" aria-label="Remove" />
                              </tr>
                            </thead>
                            <tbody>
                              {selectedObject.fields.map((f, idx) => (
                                <SemanticColumnRow
                                  key={f.id}
                                  objectId={selectedObjectId!}
                                  field={f}
                                  onApplyPatch={applyFieldColumnPatch}
                                  onRemoveField={removeFieldFromObject}
                                  onMoveField={moveFieldInObject}
                                  moveUpDisabled={idx === 0}
                                  moveDownDisabled={idx === selectedObject.fields.length - 1}
                                  removeDisabled={String(selectedObject.source?.type ?? "") === "query"}
                                  rowSelected={selectedFieldId === f.id}
                                  onSelectField={() => {
                                    setSelectedFieldId(f.id);
                                    setObjectConfigSubTab("fields");
                                  }}
                                  areaRestriction={
                                    restrictionArea
                                      ? {
                                          restriction: fieldRestriction(restrictionArea, selectedObjectId!, f),
                                          onChange: (restricted) => setFieldRestrictedInArea(selectedObjectId!, f.id, restricted)
                                        }
                                      : undefined
                                  }
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}

              {/*
                ADR-0161 — `!selectedTemplate` matters: the model-config panel is
                the fallback for "nothing selected", and a selected TEMPLATE
                object is a selection. Without this it rendered underneath the
                template inspector, stacking two panels in one frame.
              */}
              {inspectorTopTab === "config" && selectedNote && !selectedRelationship && !selectedObject && !selectedTemplate ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div className="reportSectionTitle" style={{ fontSize: 12 }}>
                    Note
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="sem-note-text">
                      Text
                    </label>
                    <textarea
                      id="sem-note-text"
                      ref={noteTextRef}
                      className="input reportTextarea"
                      maxLength={NOTE_MAX_CHARS}
                      value={noteTextDraft}
                      onChange={(e) => setNoteTextDraft(e.target.value)}
                      onBlur={() => commitNoteOnBlur()}
                    />
                    {noteCountdown(noteTextDraft) ? <div className="help">{noteCountdown(noteTextDraft)}</div> : null}
                  </div>
                </div>
              ) : null}

              {inspectorTopTab === "config" && !selectedRelationship && !selectedObject && !selectedTemplate && !selectedNote ? (
                <>
                  <div className="tabs" role="tablist" aria-label="Model config">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={modelConfigSubTab === "model"}
                      className={`tab${modelConfigSubTab === "model" ? " tabActive" : ""}`}
                      onClick={() => setModelConfigSubTab("model")}
                    >
                      Model
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={modelConfigSubTab === "subjectArea"}
                      className={`tab${modelConfigSubTab === "subjectArea" ? " tabActive" : ""}`}
                      onClick={() => setModelConfigSubTab("subjectArea")}
                    >
                      Subject area
                    </button>
                  </div>
                  <div className="tabPanel" style={{ marginTop: 8 }}>
                    {modelConfigSubTab === "model" ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="field">
                          <label className="label" htmlFor="sem-palette-status">
                            Status
                          </label>
                          <select
                            id="sem-palette-status"
                            className="input"
                            value={rowStatus}
                            onChange={(e) => onStatusChange(e.target.value as SemanticModelStatus)}
                          >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-palette-name">
                            Name
                          </label>
                          <input
                            id="sem-palette-name"
                            className="input"
                            value={modelName}
                            onChange={(e) => setModelName(e.target.value)}
                            onBlur={() => persistIfModelNameChanged()}
                          />
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-palette-desc">
                            Description
                          </label>
                          <textarea
                            id="sem-palette-desc"
                            className="input reportTextarea"
                            value={modelDescription}
                            onChange={(e) => setModelDescription(e.target.value)}
                            onBlur={() => persistIfDescriptionChanged()}
                          />
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-model-tags">
                            Tags
                          </label>
                          <input
                            id="sem-model-tags"
                            className="input"
                            value={tagsInput}
                            onChange={(e) => setTagsInput(e.target.value)}
                            onBlur={() => commitTagsOnBlur()}
                            placeholder="comma, separated"
                          />
                        </div>
                      </div>
                    ) : null}
                    {modelConfigSubTab === "subjectArea" && activeSa ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="field">
                          <label className="label" htmlFor="sem-sa-name">
                            Name
                          </label>
                          <input
                            id="sem-sa-name"
                            ref={saNameInputRef}
                            className="input"
                            disabled={activeSa.is_master}
                            value={saNameDraft}
                            onChange={(e) => setSaNameDraft(e.target.value)}
                            onBlur={() => commitSubjectAreaOnBlur()}
                          />
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-sa-desc">
                            Description
                          </label>
                          <textarea
                            id="sem-sa-desc"
                            className="input reportTextarea"
                            value={saDescDraft}
                            onChange={(e) => setSaDescDraft(e.target.value)}
                            onBlur={() => commitSubjectAreaOnBlur()}
                          />
                        </div>
                        <div className="field">
                          <label className="label" htmlFor="sem-sa-display">
                            Display mode
                          </label>
                          <select
                            id="sem-sa-display"
                            className="input"
                            value={normalizeDisplayMode(activeSa.display_mode)}
                            onChange={(e) => {
                              const copy = JSON.parse(JSON.stringify(spec)) as SemanticSpecV1;
                              const sa = copy.subject_areas.find((s) => s.id === activeSaId);
                              if (sa) sa.display_mode = e.target.value;
                              setSpec(copy);
                              clearSpecDebounce();
                              void persistNow(copy);
                            }}
                          >
                            {SEMANTIC_DISPLAY_MODES.map((m) => (
                              <option key={m} value={m}>
                                {SEMANTIC_DISPLAY_LABELS[m]}
                              </option>
                            ))}
                          </select>
                        </div>
                        {activeSa.is_master ? (
                          <div className="help">{assignedLine(true, [])}</div>
                        ) : areaAssigneesError ? (
                          <div className="help">{areaAssigneesError}</div>
                        ) : areaAssignees ? (
                          <div className="help">{assignedLine(false, areaAssignees.get(activeSa.id) ?? [])}</div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </>
          </div>
        </InspectorFlyout>
        </div>
      </ReactFlowProvider>
      {pendingSaObjectDeleteId && spec ? (
        <div
          className="modalOverlay"
          role="presentation"
          onClick={() => setPendingSaObjectDeleteId(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="semantic-sa-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="reportModalTitle" id="semantic-sa-delete-title">
              Remove object
            </div>
            <p className="muted" style={{ fontSize: 13, margin: "0 0 12px", lineHeight: 1.45 }}>
              Remove{" "}
              <strong>{spec.objects.find((o) => o.id === pendingSaObjectDeleteId)?.name ?? "this object"}</strong> from
              this subject area only (it remains in Master and the catalog), or delete it from the entire model (all
              subject areas and relationships)?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  if (!spec) return;
                  const oid = pendingSaObjectDeleteId;
                  setSpec(removePlacementFromSpec(spec, activeSaId, oid));
                  setPendingSaObjectDeleteId(null);
                  setInspectorTopTab("config");
                  setSelectedObjectId(null);
                  setSelectedRelationshipId(null);
                  setSelectedFieldId(null);
                }}
              >
                Remove from this subject area only
              </button>
              <button
                type="button"
                className="btn btnDanger"
                onClick={() => {
                  if (!spec) return;
                  const oid = pendingSaObjectDeleteId;
                  setSpec(deleteObjectFromSpec(spec, oid));
                  setPendingSaObjectDeleteId(null);
                  setInspectorTopTab("config");
                  setSelectedObjectId(null);
                  setSelectedRelationshipId(null);
                  setSelectedFieldId(null);
                }}
              >
                Remove from entire model
              </button>
              <div className="actions" style={{ justifyContent: "flex-end", marginTop: 4 }}>
                <button type="button" className="btn" onClick={() => setPendingSaObjectDeleteId(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <SemanticImportWizard
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        spec={spec as unknown as SemanticSpecImportShape}
        activeSaId={activeSaId}
        onApply={(next) => {
          // ADR-0212: a column the import replaced away takes its subject-area restrictions with it.
          setSpec(pruneRestrictionsToFields(next as SemanticSpecV1).spec);
          toast.push({ kind: "success", message: "Import applied to model." });
        }}
      />
    </PageLayout>
  );
}
